import { Fragment, useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useTableColumns } from '@/hooks/useTableColumns'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import { sortParam } from '@/lib/tableSort'
import { ColumnCells, ResizableTh } from '@/components/table'
import { useNavigate } from 'react-router-dom'
import { usePageTitle } from '@/components/layout/PageTitle'
import { Loader2, AlertCircle, Search, Check, ChevronDown, ChevronLeft, ChevronRight, HeartPulse, Plus, Repeat2, Ban } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { DateTimePanel } from '@/components/ui/date-time-panel'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { proceduresApi, type ReservationPlan,
  type CapaTarget,
  type ProcedureHolds,
} from '@/api/proceduresApi'
import { procedureTemplatesApi } from '@/api/procedureTemplatesApi'
import { patientsApi } from '@/api/patientsApi'
import type { PatientListItem } from '@/api/patientsApi'
import { STATUS_META, type StepStatus } from '@/lib/journey'
import { assignedToLabel } from '@/lib/assignedTo'
import { versionLabel } from '@/lib/versioning'
import { journeyCacheKey as journeyKey } from '@/lib/journeyCacheKey'
import {
  formatPlanned,
  fromLocalInput,
  isBeforeMin,
  isCompleteLocalInput,
  splitLocalInput,
  toLocalInput,
} from '@/lib/planningDates'
import { formatStartedOn, isOverdue, localTodayISO } from '@/lib/procedureDates'
import { PickListActionsMenu } from '@/components/pick-lists/PickListActionsMenu'
import { OverduePill } from '@/components/procedures/OverduePill'
import { OverallStatusLink } from '@/components/board/OverallStatusLink'
// proceduresApi throws the raw response text; this unwraps Nest's JSON body.
import { requestErrorMessage as apiErrorMessage } from '@/lib/requestErrorMessage'
import { deriveProcedureStepStatus, kitProgressByProcedure } from '@/lib/procedureStepStatus'
import { isRepeatingStep, journeyProgress } from '@/lib/repeats'
import { reserveOutcomeError, reservedAnything } from '@/lib/reserveOutcome'
import { AssigneeSelect } from '@/components/procedure/AssigneeSelect'
import { useAssignProcedure, useAssignStep } from '@/hooks/useProcedureAssignment'
import { useProcedureJourneys } from '@/hooks/useProcedureJourneys'
import { useProcedureAssignableUsers } from '@/hooks/useProcedureAssignableUsers'
import { PERMISSIONS, usePermission } from '@/lib/permissions'
import { useCustomersList } from '@/hooks/useCustomers'
import { CUSTOMER_CATEGORY } from '@/lib/customers'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { MesProductDialog } from '@/components/mes-products/MesProductDialog'
import { useMesProducts, usePrimaryDescription } from '@/hooks/useMesProducts'
import { productDescriptions, productKeywords, productLabel } from '@/lib/mesProducts'
import { Combobox } from '@/components/ui/combobox'
import { useInvalidateRunReservations, useInvalidateStepExecutions } from '@/hooks/useProcessSteps'
import { ScheduleReserveDialog } from '@/components/procedures/ScheduleReserveDialog'
import { MoveRunConfirmDialog } from '@/components/procedures/MoveRunConfirmDialog'
import {
  describeProcedureFilters,
  type ProcedureFilterChip,
} from '@/lib/procedureFilters'
import { LisPendingBadge } from '@/components/lis-review/LisPendingBadge'
import type {
  CapaRecord,
  ProcedureAssignee,
  Procedure,
  ProcedureStatus,
  ProcedureListStatus,
  ProcedureJourney,
  ProcedureJourneyStep,
  ProcedureTemplateListItem,
  ProcedureTemplateSequenceStep,
  MesProductSummary,
  ProcedureType,
  ProductMatch,
} from '@/types/cgt'
import { PROCEDURE_TYPE_LABELS } from '@/types/cgt'
import { capaAuthor, capaFormLabel, expandCapaRows, type CapaRowExtras } from '@/lib/capaRows'
import { CapaStatCards } from '@/components/procedure/CapaStatCards'
import type { CapaStatInput } from '@/lib/capaStats'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { adHocStepQuery, procedureRunPath, withQuery } from '@/lib/procedureRunPath'
import { CreateCapaDialog } from '@/components/procedure/CreateCapaDialog'
import { useCreateKitProcedure, useKitForms, useKitInventory } from '@/hooks/usePickLists'
import { useBackNavLinkBuilder } from '@/components/navigation/BackLink'
import {
  canSubmitProcedure,
  isValidProcedureQuantity,
  isValidProcedureQuantityEdit,
  isValidWorkOrderNumber,
  normalizeWorkOrder,
  procedureQuantityPayload,
  workOrderPayload,
  workOrderUpdatePayload,
  MAX_WORK_ORDER_LENGTH,
  WORK_ORDER_FORMAT_MESSAGE,
  KIT_PROCEDURE_TYPE,
  MES_PROCEDURE_TYPE,
  MAX_DUE_OFFSET_DAYS,
  MAX_PROCEDURE_QUANTITY,
  procedureCreateErrorField,
  templatesOfferedFor,
} from '@/lib/procedureCreate'
import type { ProcedureCreateField } from '@/lib/procedureCreate'
import { PageHeading } from '@/components/layout/PageHeading'

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

/**
 * A KIT procedure is created here like any other — the only difference is that
 * it has no patient (spec: "Patient = LOT Number"). Do NOT re-add a local copy
 * of the type list: one was kept here, drifted to the stored label
 * "KIT Creation" instead of the enum member `KitCreation`, and was then
 * "auto-fixed" by deleting the value, which silently removed Kit from this
 * dialog. PROCEDURE_TYPES in types/cgt.ts is the single source.
 */
const KIT_TYPE = KIT_PROCEDURE_TYPE
const MES_TYPE = MES_PROCEDURE_TYPE



const OVERDUE_TEXT = 'text-rose-600 font-medium'

/**
 * One planning-date cell. With procedures.manage: an inline date input that
 * commits on blur/Enter (never onChange — typing a year fires change per
 * keystroke) and clears via the input's native clear (empty → null). Without:
 * plain text, em-dash when unset. Lives inside a row that acts as a link, so
 * the td swallows clicks like the Assigned To cell above it.
 */
function DateCell({
  value,
  canManage,
  saving,
  overdue = false,
  min,
  derived = false,
  blockedReason = null,
  onReset,
  onSave,
}: {
  value: string | null
  canManage: boolean
  saving: boolean
  overdue?: boolean
  /**
   * Why a NEW date cannot be set right now, or null when one can.
   *
   * Blocks setting, never clearing — the server draws the line in exactly that
   * place, and a cell that refused both would trap a run which had a date and
   * then lost whatever made it eligible, with no way to take the date off.
   *
   * Distinct from `!canManage`, which is about permission. This is a state of
   * the RUN the operator can go and change, so the reason has to be legible
   * rather than the cell simply being inert.
   */
  blockedReason?: string | null
  /**
   * Earliest selectable instant; days before it are greyed out in the picker
   * and refused on commit.
   *
   * The Due cell passes the scheduled start, so a due date cannot precede its
   * own run — the server's `assertDateOrder` still refuses it, but an operator
   * should not be offered a time that will bounce. The Scheduled cell passes
   * the start of today, so a run cannot be planned into the past.
   */
  min?: string | null
  /**
   * This value came from the template's offset rather than being typed. Shown,
   * because "the system chose this" and "someone chose this" are different
   * facts about a date a run is judged against.
   */
  derived?: boolean
  /** Clear a hand-set date so it derives again. Absent when there is nothing to reset. */
  onReset?: () => void
  /** Resolves false when the save was rejected — the cell reverts its draft. */
  onSave: (next: string | null) => Promise<boolean>
}) {
  // Minute precision, local time: `datetime-local` rejects a value carrying
  // seconds or a Z suffix outright.
  const committed = toLocalInput(value)
  const [draft, setDraft] = useState(committed)
  const [open, setOpen] = useState(false)
  useEffect(() => setDraft(committed), [committed])

  // Blocked with nothing recorded yet: there is no date to clear, so editing
  // could only produce a value the server will refuse. Read-only, with the
  // reason as its tooltip — the operator can go and fix it, unlike `!canManage`.
  //
  // A blocked cell that DOES hold a date stays editable on purpose, so it can
  // still be cleared; `commit` is what refuses a new value there.
  if (!canManage || (blockedReason && !value)) {
    return (
      <td className='px-4 py-3 text-xs' title={blockedReason ?? undefined}>
        <div className='flex items-center gap-1.5'>
          <span
            className={cn(
              value ? '' : 'text-muted-foreground',
              overdue && OVERDUE_TEXT,
              blockedReason && 'cursor-help underline decoration-dotted underline-offset-4',
            )}
          >
            {formatPlanned(value)}
          </span>
          {overdue && <OverduePill />}
        </div>
      </td>
    )
  }

  // Only the day half is read here, to word the blocker; the panel owns both.
  const { date: draftDay } = splitLocalInput(draft)
  const dirty = draft !== committed
  // Clearing is its own kind of valid: an empty draft is not a complete instant
  // and never will be, but committing it is how a date gets removed.
  const clearing = dirty && draft === ''
  const tooEarly = isBeforeMin(draft, min)
  // Blocked cells reach the panel only when they already hold a date, and only
  // CLEARING it is allowed — the same line the server draws.
  const onlyClearable = Boolean(blockedReason)
  const applicable = dirty && (clearing || (isCompleteLocalInput(draft) && !tooEarly && !onlyClearable))

  // Why Apply is unavailable, said in the panel rather than left to a
  // disabled button the operator has to guess about.
  const blocker = !dirty
    ? null
    : onlyClearable && draft
      ? `${blockedReason} — you can only clear it`
      : tooEarly
        ? `Earliest is ${formatPlanned(min)}`
        : !isCompleteLocalInput(draft)
          ? draftDay
            ? 'Set a time as well'
            : 'Pick a day'
          : null

  function revert() {
    setDraft(committed)
    setOpen(false)
  }

  async function commit() {
    if (!applicable) return
    // Sent as an instant, not the picker's local string — the server stores a
    // timestamp and would otherwise read it as UTC.
    //
    // The panel stays OPEN until this resolves, so Apply can show a spinner and
    // a refusal lands somewhere the operator is still looking. A rejected save —
    // including a cancelled move confirmation — reverts the draft and leaves the
    // picker up to try again.
    const ok = await onSave(fromLocalInput(draft))
    if (ok) setOpen(false)
    else setDraft(committed)
  }

  // CLICK TO EDIT, rather than a permanently-mounted picker.
  //
  // Two `datetime-local` inputs per row is most of a table's width spent on
  // controls nobody is using — and the native field's widest state is the EMPTY
  // one ("dd/mm/yyyy, --:-- --" plus its own calendar button), so the columns
  // were sized for their least useful case. Reading is the common case; editing
  // is occasional and gets the space only while it is happening.
  //
  // EXPLICIT APPLY, INSIDE THE PANEL.
  //
  // This was a native `datetime-local` with a tick and a cross beside it in the
  // row, and the split is what made it awkward: the browser's own popup has no
  // notion of "done", so the operator picked a day and a time in one panel and
  // then had to find the confirm in a different place — often underneath the
  // popup that was still open. A button cannot be added to that popup; it is
  // drawn by the browser, outside the document. So the calendar is ours now, and
  // the confirmation sits in it.
  //
  // What has NOT changed is that nothing saves without a deliberate Apply. On a
  // field that re-books the run's machines and material, "I clicked away" is not
  // consent, so there is no commit-on-blur and no commit-on-select.
  return (
    <td className='px-4 py-3' onClick={e => e.stopPropagation()}>
      <div className='flex items-center gap-1.5'>
        <Popover
          open={open}
          onOpenChange={next => {
            // Opening or closing, the draft starts from what is committed.
            // Closing by any route other than Apply therefore abandons it —
            // Escape, Cancel and a click outside all mean the same thing. The
            // inline input this replaces kept an unapplied draft sitting in the
            // row; a floating panel cannot, or the row would show the committed
            // date with an invisible edit pending behind it.
            setDraft(committed)
            setOpen(next)
          }}
        >
          <PopoverTrigger asChild>
            <button
              type='button'
              disabled={saving}
              title='Click to change'
              className={cn(
                'rounded px-1 py-0.5 text-left text-xs tabular-nums transition-colors hover:bg-muted disabled:opacity-50',
                value ? '' : 'text-muted-foreground',
                overdue && OVERDUE_TEXT,
              )}
            >
              {/* Never the raw placeholder: an unset date reads as "Set", which
                  says what the control does rather than showing an empty mask. */}
              {value ? formatPlanned(value) : 'Set'}
            </button>
          </PopoverTrigger>
          <PopoverContent
            align='start'
            className='w-auto p-0'
            // The row itself is a link that opens the procedure on Enter/Space,
            // and the panel is portalled but still inside its React tree.
            onClick={e => e.stopPropagation()}
            onKeyDown={e => {
              e.stopPropagation()
              if (e.key === 'Enter') {
                e.preventDefault()
                void commit()
              }
            }}
          >
            <div>
              {/* The grid, the floor and the draft arithmetic are shared with
                  the process step's expected start — see `DateTimePanel`. Only
                  the actions below are this cell's own. */}
              <DateTimePanel
                value={draft}
                onChange={setDraft}
                min={min}
                disabled={saving}
                blocker={blocker}
              />
              <div className='flex items-center justify-between gap-2 border-t px-3 py-3'>
                {/* Empties the draft rather than saving null outright — the
                    removal is confirmed with Apply like any other change. */}
                <Button
                  type='button'
                  size='sm'
                  variant='ghost'
                  disabled={saving || !draft}
                  onClick={() => setDraft('')}
                  className='h-7 px-2 text-xs text-muted-foreground'
                >
                  Clear
                </Button>
                <div className='flex items-center gap-2'>
                  <Button
                    type='button'
                    size='sm'
                    variant='ghost'
                    disabled={saving}
                    onClick={revert}
                    className='h-7 px-2 text-xs'
                  >
                    Cancel
                  </Button>
                  <Button
                    type='button'
                    size='sm'
                    disabled={saving || !applicable}
                    onClick={() => void commit()}
                    className='h-7 gap-1.5 px-3 text-xs'
                  >
                    {saving ? (
                      <Loader2 className='h-3.5 w-3.5 animate-spin' />
                    ) : (
                      <Check className='h-3.5 w-3.5' />
                    )}
                    {clearing ? 'Clear date' : 'Apply'}
                  </Button>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
        {overdue && <OverduePill />}
      </div>
      {/* No "From the template" caption: a derived date is the normal case,
          and labelling every one of them adds a line to most rows to state
          the default. `derived` still gates Reset below — there is nothing to
          reset on a date nobody set. */}
      {!derived && onReset && value && (
        <button
          type='button'
          onClick={onReset}
          disabled={saving}
          className='mt-0.5 text-[10px] font-medium text-primary underline-offset-2 hover:underline disabled:opacity-50'
        >
          Set manually · Reset
        </button>
      )}
    </td>
  )
}

/**
 * Created By: the person's NAME, with their address on hover.
 *
 * The column was rendering `createdBy` raw, and that field is an ACTOR STRING —
 * `actorOf(user)` server-side, so an email address. Two costs, both reported:
 * an address is not how a colleague is identified on a quality record, and a
 * long unbroken one takes width from every column beside it, since it has no
 * spaces for the table to wrap at.
 *
 * The address is not thrown away, because it is what distinguishes two people
 * of the same name on a record that has to be citable — it moves to the
 * tooltip, which costs the table no width at all. `capaAuthor` decides which of
 * the two shows and whether a tooltip would add anything.
 */
function CapaAuthorCell({ capa }: { capa: CapaRecord | null }) {
  const { label, email } = capaAuthor(capa)
  const cell = 'px-4 py-3 text-xs text-muted-foreground'

  // Nothing resolved a name, so the label IS the address — hovering to be told
  // what is already on screen is noise.
  if (!email) return <td className={cell}>{label}</td>

  return (
    <td className={cell}>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* `cursor-default` over the row's own pointer: this span is a hover
              target, not a second thing to click. The row's own click still
              opens the procedure. */}
          <span className='block max-w-[12rem] cursor-default truncate'>{label}</span>
        </TooltipTrigger>
        <TooltipContent side='top'>{email}</TooltipContent>
      </Tooltip>
    </td>
  )
}

/**
 * The WO# cell, editable in place.
 *
 * A work order references a record in an OUTSIDE system, and outside systems
 * reissue and correct their numbers — so this stays editable for the life of the
 * procedure rather than being fixed at creation. Nothing downstream derives from
 * it, so a late correction is safe.
 *
 * Mirrors DateCell: same read-only fallback, same draft-revert-on-rejection, and
 * the same reason for stopping propagation (the row opens the procedure).
 */
function WorkOrderCell({
  value,
  canManage,
  saving,
  onSave,
}: {
  value: string | null
  canManage: boolean
  saving: boolean
  /** Resolves false when the save was rejected — the cell reverts its draft. */
  onSave: (next: string | null) => Promise<boolean>
}) {
  const committed = value ?? ''
  const [draft, setDraft] = useState(committed)
  useEffect(() => setDraft(committed), [committed])

  if (!canManage) {
    return (
      <td className='px-4 py-3'>
        {value ? (
          <span className='font-mono text-xs'>{value}</span>
        ) : (
          <span className='text-muted-foreground'>—</span>
        )}
      </td>
    )
  }

  async function commit() {
    if (normalizeWorkOrder(draft) === committed) return
    // Rejected locally rather than sent: the API answers 400 for the same rule,
    // but the message names the DTO field rather than what to do about it.
    if (!isValidWorkOrderNumber(draft)) {
      setDraft(committed)
      return
    }
    const ok = await onSave(workOrderUpdatePayload(draft))
    if (!ok) setDraft(committed)
  }

  return (
    <td className='px-4 py-3' onClick={e => e.stopPropagation()}>
      <Input
        value={draft}
        disabled={saving}
        maxLength={MAX_WORK_ORDER_LENGTH}
        placeholder='—'
        aria-label='Work order number'
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          e.stopPropagation()
          if (e.key === 'Enter') {
            e.preventDefault()
            e.currentTarget.blur()
          }
          if (e.key === 'Escape') {
            setDraft(committed)
            e.currentTarget.blur()
          }
        }}
        className='h-7 w-[9.5rem] px-2 font-mono text-xs'
      />
    </td>
  )
}

/**
 * How many times one form runs in one procedure — the repeat icon and its
 * number, editable in place (z8nrz7dkkc).
 *
 * Deliberately NOT the `Qty` column beside it. That one is `Procedure.quantity`:
 * how many units the run produces, which multiplies every step's per-unit
 * material. This is how many times one FORM is performed. A run of 1 unit can
 * still have a step performed twenty times, so conflating them would make one
 * of the two unexpressible.
 *
 * Lowering the number never deletes a performance already done — the server
 * refuses to discard a signed form to tidy a count — so the surplus is reported
 * back and said out loud rather than left to be noticed.
 */
function StepRepeatCell({
  procedureId,
  step,
  onSaved,
}: {
  procedureId: string
  step: ProcedureJourneyStep
  onSaved: () => void
}) {
  const total = step.repeatCount ?? 1
  const [draft, setDraft] = useState(String(total))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // The server is the authority: re-sync when a reload brings a new number,
  // including the one this cell just sent.
  useEffect(() => setDraft(String(total)), [total])

  const commit = async () => {
    const next = Number.parseInt(draft, 10)
    // An empty or unparseable box reverts rather than sending something the
    // DTO would reject — same rule as the Qty cell beside it.
    if (Number.isNaN(next) || next < 1) {
      setDraft(String(total))
      return
    }
    if (next === total) return
    setSaving(true)
    setError(null)
    try {
      const res = await proceduresApi.setStepRepeats(procedureId, step.stepId, next)
      if (res.surplusSubmissions > 0) {
        setError(
          `${res.surplusSubmissions} already-completed repeat(s) are above the new count and were kept.`,
        )
      }
      onSaved()
    } catch (e) {
      setDraft(String(total))
      setError(apiErrorMessage(e, 'Failed to update repeats.'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <span className='flex shrink-0 items-center gap-1' title='How many times this form is performed'>
      <Repeat2 className='h-3.5 w-3.5 text-muted-foreground' />
      <Input
        type='number'
        min={1}
        step={1}
        disabled={saving}
        className='h-6 w-14 text-xs'
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={() => void commit()}
        onKeyDown={e => {
          if (e.key === 'Enter') e.currentTarget.blur()
          if (e.key === 'Escape') setDraft(String(total))
        }}
      />
      {error && <span className='text-[11px] text-amber-600'>{error}</span>}
    </span>
  )
}

/**
 * The Qty cell, editable in place.
 *
 * Unlike the work order beside it, this one is NOT freely editable. Quantity
 * multiplies every step's inventory requirement and that product is read live,
 * so once lots have been allocated an edit would retroactively redefine what was
 * already picked. The server owns that rule and answers 409; this cell only has
 * to surface the refusal and put the old number back.
 *
 * Empty is not a value here — a procedure always produces some number of units —
 * so a cleared box reverts rather than sending null, which is the one way this
 * differs from WorkOrderCell.
 */
function QuantityCell({
  value,
  canManage,
  saving,
  onSave,
}: {
  value: number
  canManage: boolean
  saving: boolean
  /** Resolves false when the save was rejected — the cell reverts its draft. */
  onSave: (next: number) => Promise<boolean>
}) {
  const committed = String(value)
  const [draft, setDraft] = useState(committed)
  useEffect(() => setDraft(committed), [committed])

  if (!canManage) {
    return <td className='px-4 py-3 tabular-nums'>{value}</td>
  }

  async function commit() {
    if (draft === committed) return
    // Blank, 0, 1.5, or anything past the cap: revert rather than send. The API
    // rejects the same values, but with a message naming the DTO field.
    if (!isValidProcedureQuantityEdit(draft)) {
      setDraft(committed)
      return
    }
    const ok = await onSave(Number(draft.trim()))
    if (!ok) setDraft(committed)
  }

  return (
    <td className='px-4 py-3' onClick={e => e.stopPropagation()}>
      <Input
        type='number'
        min={1}
        max={MAX_PROCEDURE_QUANTITY}
        step={1}
        value={draft}
        disabled={saving}
        aria-label='Quantity'
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          e.stopPropagation()
          if (e.key === 'Enter') {
            e.preventDefault()
            e.currentTarget.blur()
          }
          if (e.key === 'Escape') {
            setDraft(committed)
            e.currentTarget.blur()
          }
        }}
        className='h-7 w-[5rem] px-2 text-xs tabular-nums'
      />
    </td>
  )
}

/**
 * A row as RENDERED: a procedure, plus which of its CAPAs this row is.
 *
 * `rowKey` and not `id` is what identifies a row in the table — two CAPAs in one
 * run share the procedure id — so React keys, the expand toggle and the pick-list
 * menu all key on it. Handlers that act on the PROCEDURE (work order, quantity,
 * assignment, delete) still take `id`, because that is what they change.
 */
type RenderedRow = ProcedureRow & CapaRowExtras


/**
 * "Matched product S11284-A0 (aliquot)" under a row a DIN search found.
 *
 * Renders nothing at all when the row was not reached by a product, which is
 * every row of an ordinary patient/MRN search (AC2.3).
 */
function ProductMatchNote({ matches }: { matches?: ProductMatch[] }) {
  if (!matches || matches.length === 0) return null
  return (
    <div className='mt-0.5 space-y-0.5'>
      {matches.map(m => (
        <div
          key={`${m.din}:${m.descriptionCode}`}
          className='font-mono text-[11px] text-muted-foreground'
          title={m.isAliquot ? 'Aliquot produced by a split' : 'Received product'}
        >
          {/* The suffix is part of the identifier the operator is holding, so
              it is joined to the DIN rather than shown as a separate token. */}
          {m.descriptionCode ? `${m.din}-${m.descriptionCode}` : m.din}
          <span className='ml-1 not-italic'>{m.isAliquot ? '(aliquot)' : '(received)'}</span>
        </div>
      ))}
    </div>
  )
}

interface ProcedureRow {
  id: string
  /** Null for a run with no patient; the cell renders a dash (z8nrz7ckj9). */
  mrn: string | null
  /** The MES order's minted handle. Null on every other type. */
  mfgNumber: string | null
  /**
   * The CAPAs raised inside this run, on the CAPA page only. `expandCapaRows`
   * fans the row out over them — one row per CAPA — so the rendered row reads
   * its own from `capa`, not from here.
   */
  capaRecords?: CapaRecord[]
  /** The host run's kind, so a CAPA row can open the page its run lives on. */
  procedureTemplateType: ProcedureType
  patientName: string | null
  procedureTemplateId: string
  procedureTemplateName: string
  procedureTemplateVersion: number
  /**
   * Minted for Transplants; may also be typed in on any row to match a number
   * issued outside this system. Null means "none yet", not missing data.
   */
  workOrderNumber: string | null
  /** Kit and MES only (z8nrz7e714); null when the run has none. */
  customerName: string | null
  /** MES only (z8nrz7fvhx): the Product Code the run makes; null when it has none. */
  mesProduct: MesProductSummary | null
  /** Units this procedure produces; multiplies its steps' inventory needs. */
  quantity: number
  /** The procedure's own status — the overdue rule needs it (stepStatus is journey-derived). */
  status: ProcedureStatus
  startedAt: string
  scheduledDate: string | null
  dueDate: string | null
  /** The due date was set by hand, so re-scheduling leaves it alone. */
  dueDateOverridden: boolean
  completed: number
  total: number
  stepStatus: StepStatus
  stepLabel: string
  assignedToLabel: string
  /** The uniform procedure assignee, when there is one — preselects the picker. */
  procedureAssignee: ProcedureAssignee | null
  // `isAssigned` was here to gate the Scheduled cell, mirroring a server rule
  // that no longer exists: a date commits no resources, so it needs no owner,
  // and reserving — which does commit them — now claims an owner instead of
  // refusing. Nothing reads it any more, so it is gone rather than left to
  // suggest a gate that isn't there.
  /**
   * Whether the template has a Process field anywhere, and so whether a pick
   * list can mean anything for this procedure. Gates the `+`.
   */
  hasProcessStep: boolean
  /**
   * Which product a DIN search matched (z8nrz7dkzy AC2.3).
   *
   * Present ONLY on a row the server reached through a product; absent on one
   * reached by patient, MRN or date of birth. Presence is therefore the signal
   * — there is no separate "matched by" flag to read.
   */
  productMatches?: ProductMatch[]
}

/**
 * Columns for one variant of this table.
 *
 * Keyed per variant rather than shared, because the variants do not have the
 * same columns: CAPA adds Form and Created By, and the leading identifier is a
 * Patient, a Kit ID, an MFG ID or an Event ID depending on which page you are
 * on. One shared layout would let a choice made on CAPA describe a table that
 * does not have those columns.
 *
 * The expand chevron, the leading identifier and the actions cell are locked —
 * the chevron opens the run's forms, the identifier is how a row is recognized,
 * and the actions are the only route to delete.
 *
 * `sortable` columns sort SERVER-side (z8nrz7e4h6): this list is paged, so the
 * ids here are the backend's allowed sort keys (procedureSorts in
 * procedures.service). Not sortable, because no stored column holds what they
 * show: Progress and Assigned To are derived per row, and on CAPA the Event ID,
 * Form and Created By belong to each CAPA, of which a run can hold several.
 */
function proceduresColumns(opts: {
  leadHeader: string
  isCapa: boolean
  /** Kit and MES runs carry an optional customer (z8nrz7e714). */
  withCustomer: boolean
  /** MES runs carry an optional Product Code (z8nrz7fvhx). */
  withProduct: boolean
  /**
   * The Transplants (clinical) list. WO# and Qty are not used there
   * (z8nrz7dych), so it does not show them; Kits, MES and CAPA still do.
   */
  clinical: boolean
}): TableColumnDef[] {
  const { leadHeader, isCapa, withCustomer, withProduct, clinical } = opts
  return [
    { id: 'expand', header: 'Forms', locked: true },
    { id: 'lead', header: leadHeader, locked: true, sortable: !isCapa },
    { id: 'procedure', header: 'Procedure', sortable: true },
    // Between Procedure and Customer, as the spec's mock has them; no stored
    // column orders by what they show, so neither sorts.
    ...(withProduct
      ? [
          { id: 'productCode', header: 'Product Code' },
          { id: 'productDescription', header: 'Product Description' },
        ]
      : []),
    ...(withCustomer ? [{ id: 'customer', header: 'Customer' }] : []),
    ...(isCapa ? [{ id: 'form', header: 'Form' }] : []),
    ...(clinical
      ? []
      : [
          { id: 'workOrder', header: 'WO#', sortable: true },
          { id: 'quantity', header: 'Qty', sortable: true },
        ]),
    { id: 'progress', header: 'Progress' },
    // Orders by the run's stored status; the badge may refine it (kit build,
    // step progress, pending review).
    { id: 'status', header: 'Status', sortable: true },
    ...(isCapa ? [{ id: 'createdBy', header: 'Created By' }] : []),
    { id: 'assignedTo', header: 'Assigned To' },
    { id: 'started', header: 'Started', sortable: true },
    { id: 'scheduled', header: 'Scheduled', sortable: true },
    { id: 'due', header: 'Due', sortable: true },
    { id: 'actions', header: 'Actions', locked: true },
  ]
}

// Header classes this table already used. Every data column shares one, and the
// two chrome columns have their own.
const PROCEDURE_HEAD_CLASSES: Record<string, string> = {
  expand: 'w-8 px-2 py-3',
  actions: 'px-4 py-3',
}
const PROCEDURE_HEAD_DEFAULT = 'px-4 py-3 font-medium'

/**
 * Every procedure — patient care, or kit runs.
 *
 * A kit run IS a procedure (spec p2/p5), so the Kits page is this table in
 * `kit` variant rather than a lookalike: same expand rows, progress, status,
 * Assigned To, dates and pagination, guaranteed identical because it is the
 * same component. The Patient column carries the kit supply id, since for a
 * kit "Patient = LOT Number" (p3).
 */
/**
 * A refusal rendered against the input that caused it (z8nrz7dyc2, AC3.1).
 *
 * `role='alert'` because the operator's attention is on the control they just
 * used, not on wherever the message lands — and on a form this long the two are
 * often not on screen together.
 */
function CreateFieldError({ show, message }: { show: boolean; message: string | null }) {
  if (!show || !message) return null
  return (
    <p role='alert' className='flex items-start gap-1.5 text-xs text-destructive'>
      <AlertCircle className='mt-px h-3.5 w-3.5 shrink-0' />
      {message}
    </p>
  )
}

export function ProceduresPage({
  variant = 'patient',
}: { variant?: 'patient' | 'kit' | 'mes' | 'capa' } = {}) {
  const isKit = variant === 'kit'
  // An MES order is a procedure too (z8nrz7ckj9), so it is a third variant of this
  // table rather than a fourth lookalike. It differs from `kit` in two ways: it
  // is selected by TEMPLATE TYPE rather than by form category, and its first
  // column is the minted MFG number rather than a supply id.
  const isMes = variant === 'mes'
  /**
   * CAPA is a fourth variant, and the only one that is a LENS rather than a
   * kind of work: it lists procedures that are CAPA-typed OR carry a form of
   * category `capa`, and those same procedures still appear on their own page.
   * So there is no "create" here — a CAPA is raised by adding a CAPA form to a
   * case, not by starting a procedure from this table.
   */
  const isCapa = variant === 'capa'
  // Per-user column layout, scoped to this variant — see proceduresColumns.
  const leadHeader = isKit ? 'Kit ID' : isMes ? 'MFG ID' : isCapa ? 'Event ID' : 'Patient'
  const columnDefs = useMemo(
    () =>
      proceduresColumns({
        leadHeader,
        isCapa,
        withCustomer: isKit || isMes,
        withProduct: isMes,
        clinical: variant === 'patient',
      }),
    [leadHeader, isCapa, isKit, isMes, variant],
  )
  const cols = useTableColumns(`procedures-${variant}`, columnDefs)
  /**
   * Every user-facing noun on this page, resolved from the variant in one place.
   *
   * These were inline `isKit ? … : …` ternaries, which meant the FALSE branch
   * was the transplant wording — so adding a third variant made an MES order call
   * itself a transplant in the heading, the buttons, the search box, the empty
   * states and the delete confirmation (z8nrz7ckj9). A lookup cannot inherit a
   * default that way: a new variant has to say what it is called.
   */
  const copy = isKit
    ? {
        title: 'Kits',
        plural: 'kits',
        one: 'kit',
        create: 'Create kit order',
        blurb: 'Every kit run. Open one to work its forms.',
        search: 'Search by lot number or kit…',
        pickTemplate: 'Select a kit template',
        customer: 'Customer',
      }
    : isMes
      ? {
          title: 'MES',
          plural: 'MES orders',
          one: 'MES order',
          create: 'Create MES order',
          blurb: 'Every manufacturing order. Open one to work its forms.',
          search: 'Search by MFG number or template…',
          pickTemplate: 'Select a template',
          customer: 'Customer',
        }
      : isCapa
        ? {
            title: 'CAPA',
            plural: 'CAPAs',
            one: 'CAPA',
            create: 'Create CAPA',
            // One row is one CAPA — a CAPA form raised inside a run — so the
            // page says that rather than describing the runs that hold them.
            blurb: 'Every CAPA raised, with its run and form.',
            search: 'Search by patient, MRN, or procedure…',
            pickTemplate: 'Select a template',
            // Null = this variant has no customer field at all (z8nrz7e714).
            customer: null,
          }
        : {
            title: 'Transplants',
            plural: 'transplants',
            one: 'transplant',
            create: 'Create transplant',
            blurb: 'Every transplant across patients. Open one to work its forms.',
            // Names the new terms, per AC2.10 option (a): one box, no field
            // selector, and an ambiguous term is matched against every
            // interpretation rather than the box guessing one.
            search: 'Search by patient, MRN, transplant, DIN, or DOB…',
            pickTemplate: 'Select a template',
            customer: null,
          }
  const pageTitle = copy.title
  // The top bar reads "Transplants - Kits" etc.; the single-crumb breadcrumb
  // that repeated the header here is gone (z8nrz7e46x).
  usePageTitle(pageTitle)
  const pageNoun = copy.plural
  const navigate = useNavigate()
  // A procedure page is reachable from here AND from the patient journey, so
  // it cannot infer its origin — hand it this list's live url (z8nrz7czj8).
  const withOrigin = useBackNavLinkBuilder('Procedures')
  // The patients the "filter by patient" dropdown offers — every patient this
  // page's list holds, not just the ones on the current page — with their
  // names when the server can say them.
  const [patientOptions, setPatientOptions] = useState<{ mrn: string; name: string | null }[]>([])
  const [pagedProcedures, setPagedProcedures] = useState<Procedure[]>([])
  const [pagedTotal, setPagedTotal] = useState(0)
  // The table waits for its own page and nothing else (z8nrz7e8kw): the
  // dropdown and dialog helpers load beside it.
  const [pageLoading, setPageLoading] = useState(true)
  const [procedureTemplates, setProcedureTemplates] = useState<ProcedureTemplateListItem[]>([])
  const [allPatients, setAllPatients] = useState<PatientListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [patientFilter, setPatientFilter] = useState<string>('all')
  // Server-side filters. Held as the raw control values ('' / 'all' meaning
  // "no filter") and translated at the request boundary, so a cleared control
  // and an unset filter are the same thing without a second representation.
  const [createdFrom, setCreatedFrom] = useState('')
  const [createdTo, setCreatedTo] = useState('')
  const [assigneeFilter, setAssigneeFilter] = useState<string>('all')
  // Kits and MES only: whose runs (z8nrz7e71k). 'all' = every customer.
  const [customerFilter, setCustomerFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [skip, setSkip] = useState(0)
  const [take, setTake] = useState(20)

  // Create procedure dialog state
  // Only one actions menu may be open at a time; independent per-row state let
  // several stay open simultaneously. Keyed `<rowId>` for a procedure's menu and
  // `<rowId>:<stepId>` for a form's, so a row and one of its forms cannot both
  // be open either.
  const [openMenuRowId, setOpenMenuRowId] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const createKitProcedure = useCreateKitProcedure()
  const [patientSearch, setPatientSearch] = useState('')
  const [patientDropdownOpen, setPatientDropdownOpen] = useState(false)
  const [selectedPatientMrn, setSelectedPatientMrn] = useState('')
  const [selectedProcedureTemplateId, setSelectedProcedureTemplateId] = useState('')
  const [createScheduledDate, setCreateScheduledDate] = useState('')
  const [createDueDate, setCreateDueDate] = useState('')
  // Days after the scheduled start this run is due. A STRING, not a number: the
  // field has a meaningful empty state ("use the template's"), and 0 is a real
  // value, so a numeric state would have to carry null alongside it.
  const [createDueOffset, setCreateDueOffset] = useState('')
  // Held as the raw string so clearing the box to retype does not snap to 1.
  const [createQuantity, setCreateQuantity] = useState('1')
  // Optional customer for a Kit or MES run (z8nrz7e714); '' = none.
  const [createCustomerId, setCreateCustomerId] = useState('')
  const [createMesProductId, setCreateMesProductId] = useState('')
  // The Product Information dialog, opened from a row's Product Code (z8nrz7fvhx).
  const [productInfo, setProductInfo] = useState<{ id: string; mfgNumber: string | null } | null>(null)
  // Blank means "let the server decide": mint one for a Transplant, none otherwise.
  const [createWorkOrder, setCreateWorkOrder] = useState('')
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  /**
   * Which input the current refusal is about, or null for a dialog-level one.
   *
   * Paired with `createError` rather than folded into it because almost every
   * site that touches it only CLEARS it — see `clearCreateError`.
   */
  const [createErrorField, setCreateErrorField] = useState<ProcedureCreateField | null>(null)

  function clearCreateError() {
    setCreateError(null)
    setCreateErrorField(null)
  }

  /**
   * Refuse the submit, naming the input at fault so the message renders beside
   * it (z8nrz7dyc2, AC3.1). `null` puts it in the banner under the form.
   */
  function failCreate(message: string, field: ProcedureCreateField | null) {
    setCreateError(message)
    setCreateErrorField(field)
  }
  const [templateSearch, setTemplateSearch] = useState('')
  const [debouncedTemplateSearch, setDebouncedTemplateSearch] = useState('')
  const [searchedTemplates, setSearchedTemplates] = useState<ProcedureTemplateListItem[]>([])
  /**
   * The runs a CAPA may be raised against, and the one chosen in the dialog.
   *
   * Fetched only on the CAPA page: it is the one variant whose create needs a
   * second list, and the endpoint deliberately spans transplants, kit runs and
   * MES orders — which the ordinary list excludes.
   */
  const [capaTargets, setCapaTargets] = useState<CapaTarget[]>([])
  const [templateSearchLoading, setTemplateSearchLoading] = useState(false)
  const [templateDropdownOpen, setTemplateDropdownOpen] = useState(false)
  const patientSearchRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const templateDropdownRef = useRef<HTMLDivElement>(null)

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    // MUST carry the same kitOnly polarity as refreshPage(): the two lists
    // feed the same table, and the backend EXCLUDES kit runs by default.
    // Without it the Kits page listed clinical procedures it never renders.
    const listScope = { kitOnly: isKit, type: isMes ? ('MES' as const) : undefined, capaOnly: isCapa }
    Promise.all([
      // The patients with their names, named by the server (z8nrz7e8kw) — the
      // page used to download every submission in the tenant to name them.
      // Against an older backend: the distinct MRNs, unnamed, and before that
      // endpoint existed, the full list.
      proceduresApi.patientOptions(listScope).catch(() =>
        proceduresApi
          .patientMrns(listScope)
          .catch(() =>
            proceduresApi
              .listAll(listScope)
              .then(({ items }) => items.flatMap(t => (t.patientMrn === null ? [] : [t.patientMrn]))),
          )
          .then(mrns => mrns.map(mrn => ({ mrn, name: null }))),
      ),
      isCapa ? proceduresApi.listCapaTargets() : Promise.resolve([] as CapaTarget[]),
    ])
      .then(([options, targets]) => {
        setPatientOptions(options)
        setCapaTargets(targets)
      })
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load procedures.'))
      .finally(() => setLoading(false))
  }, [isKit, isMes, isCapa])

  useEffect(() => {
    load()
  }, [load])

  /**
   * The create dialog's patient typeahead is the only reader of the patient
   * list, so it loads when that dialog opens rather than holding up the table
   * (z8nrz7e8kw). Fetched on every open, as it was on every visit before.
   */
  const [allPatientsLoading, setAllPatientsLoading] = useState(false)
  const patientsRequest = useRef(0)
  const loadCreatePatients = useCallback(() => {
    const request = ++patientsRequest.current
    setAllPatientsLoading(true)
    patientsApi
      .list({ take: 1000 })
      .then(({ items }) => {
        if (request === patientsRequest.current) setAllPatients(items)
      })
      .catch(e => {
        if (request === patientsRequest.current) {
          failCreate(e instanceof Error ? e.message : 'Failed to load patients.', 'patient')
        }
      })
      .finally(() => {
        if (request === patientsRequest.current) setAllPatientsLoading(false)
      })
  }, [])

  // Debounce template search input for server-side filtering.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedTemplateSearch(templateSearch), 300)
    return () => clearTimeout(id)
  }, [templateSearch])

  useEffect(() => {
    let cancelled = false
    setTemplateSearchLoading(true)
    procedureTemplatesApi
      .list({ offerable: true, search: debouncedTemplateSearch || undefined })
      .then(items => {
        if (cancelled) return
        setSearchedTemplates(items)
        // The unfiltered answer IS the creation surface (one row per family,
        // current ACTIVE version), so it is not fetched a second time.
        if (!debouncedTemplateSearch) setProcedureTemplates(items)
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setTemplateSearchLoading(false) })
    return () => { cancelled = true }
  }, [debouncedTemplateSearch])

  /**
   * Progress journeys for the rows ON SCREEN — not for every procedure in the
   * tenant.
   *
   * There is no batch journey endpoint, so each one is a request. Fetching them
   * for the whole tenant fired one burst of N (35 here) against the API's
   * per-user, per-endpoint rate limit, and two reloads were enough to earn a 429 — which is
   * exactly what the Kits page was doing. The table only ever renders `take`
   * rows, so the other N-take responses were thrown away anyway.
   *
   * Cached by (patient, template) in the query cache, so paging back is free
   * and so is coming back from a case (z8nrz7e1c2). Patient-less runs are
   * fetched too, by procedure id (z8nrz7ckj9) — see `journeyRequests`.
   */
  // Only for rows the server sent no `progress` for — an older backend. Against
  // a current one this fires nothing: the page arrives with its progress.
  const journeySubjects = useMemo(
    () => pagedProcedures.filter(p => p.progress === undefined),
    [pagedProcedures],
  )
  const journeyByKey = useProcedureJourneys(journeySubjects)

  // Debounce search → reset page
  useEffect(() => {
    if (search === debouncedSearch) return
    const id = setTimeout(() => { setDebouncedSearch(search); setSkip(0) }, 300)
    return () => clearTimeout(id)
  }, [search, debouncedSearch])

  // Reset page when patient filter or page size changes
  // Any filter change returns to page 1 — the same rule the debounced search
  // already follows. Staying on page 4 of a narrower result set shows an empty
  // table that looks like "no matches".
  useEffect(() => {
    setSkip(0)
  }, [patientFilter, take, createdFrom, createdTo, assigneeFilter, customerFilter, statusFilter])

  // A new sort is a new ordering of the whole list, so page 1 again (z8nrz7e4h6).
  const sort = sortParam(cols.sort)
  const [sortOfPage, setSortOfPage] = useState(sort)
  if (sortOfPage !== sort) {
    setSortOfPage(sort)
    setSkip(0)
  }

  // Reactive paginated table data (also re-run after an assignment change so
  // the derived Assigned To column stays fresh).
  const refreshPage = useCallback(() => {
    return proceduresApi.listAll({
      q: debouncedSearch || undefined,
      patientMrn: patientFilter !== 'all' ? patientFilter : undefined,
      // Sent to the server rather than applied to `items` here: the table is
      // server-paged, so filtering the page in the browser would hide matches
      // on other pages and leave the count disagreeing with the rows.
      createdFrom: createdFrom || undefined,
      createdTo: createdTo || undefined,
      assigneeId: assigneeFilter !== 'all' ? assigneeFilter : undefined,
      customerId: customerFilter !== 'all' ? customerFilter : undefined,
      status: statusFilter !== 'all' ? (statusFilter as ProcedureListStatus) : undefined,
      skip,
      take,
      sort,
      kitOnly: isKit,
      type: isMes ? 'MES' : undefined,
      capaOnly: isCapa,
      // Each row's step counts and patient name, in the same response
      // (z8nrz7e8kw) — not a journey request per row, nor every submission in
      // the tenant to work out names.
      progress: true,
      patientNames: true,
    })
      .then(({ items, total }) => {
        setPagedProcedures(items)
        setPagedTotal(total)
      })
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load procedures.'))
      .finally(() => setPageLoading(false))
  }, [
    debouncedSearch, patientFilter, createdFrom, createdTo, assigneeFilter, customerFilter, statusFilter,
    skip, take, sort, isKit, isMes, isCapa,
  ])

  useEffect(() => {
    refreshPage()
  }, [refreshPage])

  // After a write: the rows carry their progress and names now (z8nrz7e8kw),
  // so the page is refetched along with the helpers.
  const reloadAll = useCallback(() => {
    load()
    void refreshPage()
  }, [load, refreshPage])

  // ── Assign To ──────────────────────────────────────────────────────────────

  const { can } = usePermission()
  const clearFilters = useCallback(() => {
    setCreatedFrom('')
    setCreatedTo('')
    setAssigneeFilter('all')
    setCustomerFilter('all')
    setStatusFilter('all')
    // The search is part of "clear all" (z8nrz7dkzy AC2.11). It was left out
    // while the box only matched a patient or an MRN, where an active search
    // was self-evident from the rows. Now that one term can also be a DIN or a
    // date of birth, a Clear that silently leaves it applied is the exact
    // confusion AC2.11 guards against.
    setSearch('')
  }, [])

  const canAssign = can(PERMISSIONS.proceduresAssign)

  // The Customer select on the Kit and MES create dialogs (z8nrz7e714). Only
  // fetched where the field exists, and only for someone who can read the list
  // — the field is optional, so without that permission it is simply absent
  // rather than a control whose every open 403s.
  const canPickCustomer = !!copy.customer && can(PERMISSIONS.customersRead)
  // The list of names a customer is picked from: the partners in the Customer
  // category (z8nrz7earp). A run already linked to another partner keeps
  // showing its name, from the run itself.
  const customersQuery = useCustomersList({ enabled: canPickCustomer, categories: CUSTOMER_CATEGORY })
  const customerOptions = useMemo(
    () => (customersQuery.data ?? []).map(c => ({ value: c.id, label: `${c.code} — ${c.name}` })),
    [customersQuery.data],
  )
  // The Product select on Create MES order (z8nrz7fvhx): the tenant's ACTIVE
  // Product Codes. Anyone who can start a run may read them for this.
  const canPickProduct = isMes && (can(PERMISSIONS.productCodesRead) || can(PERMISSIONS.proceduresSchedule))
  const productsQuery = useMesProducts({ active: true, enabled: canPickProduct })
  const { value: primaryDescription } = usePrimaryDescription()
  // A Combobox, not the SearchableSelect beside it: its list renders inside
  // the dialog, so typing reaches its search. Either description finds a
  // product, and the other one shows on hover.
  const productOptions = useMemo(
    () =>
      (productsQuery.data ?? []).map(p => ({
        value: p.id,
        label: productLabel(p, primaryDescription),
        keywords: productKeywords(p),
        hint: productDescriptions(p, primaryDescription).other || undefined,
      })),
    [productsQuery.data, primaryDescription],
  )
  const canManage = can(PERMISSIONS.proceduresManage)
  const [today, setToday] = useState(localTodayISO())
  // Earliest plannable instant: local midnight today, NOT "now". A run booked
  // at 2pm for 9am this morning is ordinary; one booked for last Tuesday is a
  // typo. Written `T00:00` so it parses as LOCAL midnight — a bare date string
  // parses as UTC and lands on the wrong day west of Greenwich. Follows the
  // midnight rollover above with the rest of the page.
  const todayStart = `${today}T00:00`
  const { users: assignableUsers } = useProcedureAssignableUsers(canAssign)

  // Removing one chip. Bound here rather than returned from
  // describeProcedureFilters because only the page owns the setters — the
  // description of a filter and the ability to clear it are different concerns.
  const clearFilter = (key: ProcedureFilterChip['key']) => {
    if (key === 'status') setStatusFilter('all')
    if (key === 'assignee') setAssigneeFilter('all')
    if (key === 'customer') setCustomerFilter('all')
    if (key === 'raised') {
      setCreatedFrom('')
      setCreatedTo('')
    }
  }

  const activeFilters = useMemo(
    () =>
      describeProcedureFilters(
        { status: statusFilter, assigneeId: assigneeFilter, createdFrom, createdTo, customerId: customerFilter },
        assignableUsers,
        customerOptions,
      ),
    [statusFilter, assigneeFilter, createdFrom, createdTo, customerFilter, assignableUsers, customerOptions],
  )
  /**
   * Whether to offer "Clear filters".
   *
   * Counts the search separately from `activeFilters`, which drives the filter
   * CHIPS via the shared `describeProcedureFilters` — the search does not get a
   * chip (its own box already shows it, which is AC2.11's "visible"), but it
   * does have to make the clear-all reachable. Without this, a search-only
   * filter left the button hidden and the search stuck on.
   */
  const hasActiveFilters = activeFilters.length > 0 || search.trim().length > 0

  useEffect(() => {
    function scheduleNextMidnight() {
      const now = new Date()
      const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)
      const msUntilMidnight = tomorrow.getTime() - now.getTime()
      return setTimeout(() => {
        setToday(localTodayISO())
        scheduleNextMidnight()
      }, msUntilMidnight)
    }
    const timer = scheduleNextMidnight()
    return () => clearTimeout(timer)
  }, [])

  // Expanded row → that procedure's journey (fetched WITH the procedure id so the
  // per-form assignees are populated; the progress journeys above are shared
  // per (patient, procedure) and don't carry them).
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [procedureJourneys, setProcedureJourneys] = useState<Map<string, ProcedureJourney>>(new Map())
  const [procedureJourneyLoading, setProcedureJourneyLoading] = useState<string | null>(null)

  const loadProcedureJourney = useCallback((row: ProcedureRow) => {
    // No patient is not a reason to skip this (z8nrz7ckj9). This used to return
    // early for a patient-less row, and the render has no branch for "never
    // fetched" — so an expanded MES order fell through to "Couldn't load this
    // MES order's forms" while its template had forms all along. The endpoint
    // takes procedureId as a complete scope, which is passed below regardless.
    setProcedureJourneyLoading(row.id)
    return procedureTemplatesApi
      .journey(row.procedureTemplateId, row.mrn, row.id)
      .then(j => setProcedureJourneys(prev => new Map(prev).set(row.id, j)))
      .catch(console.error)
      .finally(() => setProcedureJourneyLoading(current => (current === row.id ? null : current)))
  }, [])

  function toggleExpand(row: RenderedRow) {
    // Keyed on the ROW, so expanding one CAPA does not expand the sibling CAPA
    // in the same run. The journey it loads is still the run's, cached by
    // procedure id, so two sibling rows share one fetch.
    if (expandedId === row.rowKey) {
      setExpandedId(null)
      return
    }
    setExpandedId(row.rowKey)
    if (!procedureJourneys.has(row.id)) loadProcedureJourney(row)
  }

  // Which inline picker is mid-save: the procedure id, or `${procedureTemplateId}:${stepId}`.
  // Assignment changes who owns each process step, which the step cards read
  // from their own cached query — see the hook's note.
  const invalidateStepExecutions = useInvalidateStepExecutions()
  const invalidateRunReservations = useInvalidateRunReservations()
  // Mutations rather than direct api calls — see useProcedureAssignment.
  const assignProcedure = useAssignProcedure()
  const assignStep = useAssignStep()
  const [assignSavingKey, setAssignSavingKey] = useState<string | null>(null)
  const [assignError, setAssignError] = useState<string | null>(null)

  async function saveProcedureAssign(row: ProcedureRow, assigneeId: string | null) {
    if ((row.procedureAssignee?.id ?? null) === assigneeId && row.assignedToLabel !== 'Multiple') return
    setAssignSavingKey(row.id)
    setAssignError(null)
    try {
      await assignProcedure.mutateAsync({ id: row.id, assigneeId })
      await Promise.all([
        refreshPage(),
        procedureJourneys.has(row.id) ? loadProcedureJourney(row) : Promise.resolve(),
      ])
      // This page's own state is refreshed above, but the process-step cards
      // elsewhere read a cached query with a five-minute staleTime — so
      // navigating to a case after assigning here would show the old owner.
      invalidateStepExecutions()
    } catch (e) {
      setAssignError(e instanceof Error ? e.message : 'Failed to assign the procedure.')
    } finally {
      setAssignSavingKey(null)
    }
  }

  // ── Planning dates (scheduled / due) ───────────────────────────────────────

  // `${caseId}:${field}` of the date cell mid-save.
  const [dateSavingKey, setDateSavingKey] = useState<string | null>(null)
  const [dateError, setDateError] = useState<string | null>(null)

  // ── Confirming a schedule change before it is written ──────────────────────
  //
  // A scheduled date is no longer just a date: changing it releases and re-takes
  // the run's equipment and material. That is invisible from a date cell, so it
  // is stated and agreed to BEFORE the write rather than discovered after it.
  // Scoped to `scheduledDate` — a due date books nothing.
  const [moveConfirm, setMoveConfirm] = useState<{
    row: ProcedureRow
    to: string | null
    resolve: (ok: boolean) => void
  } | null>(null)
  const [moveHolds, setMoveHolds] = useState<ProcedureHolds | null>(null)
  const [moveHoldsLoading, setMoveHoldsLoading] = useState(false)

  /**
   * Open the confirm and resolve with the operator's answer.
   *
   * The dialog is shown FIRST and the counts fetched into it, so the click
   * registers immediately instead of after a round trip. A failed count is not
   * a failed reschedule — the dialog still offers the move, just without the
   * impact line.
   */
  function confirmMove(row: ProcedureRow, to: string | null): Promise<boolean> {
    setMoveHolds(null)
    setMoveHoldsLoading(true)
    proceduresApi
      .holds(row.id)
      .then(setMoveHolds)
      .catch(() => setMoveHolds(null))
      .finally(() => setMoveHoldsLoading(false))
    return new Promise<boolean>(resolve => setMoveConfirm({ row, to, resolve }))
  }

  function answerMove(ok: boolean) {
    moveConfirm?.resolve(ok)
    setMoveConfirm(null)
  }

  async function saveDate(
    row: ProcedureRow,
    field: 'scheduledDate' | 'dueDate',
    value: string | null,
  ): Promise<boolean> {
    // Before the saving flag, so a cancelled confirm leaves the cell untouched
    // rather than flashing a save that never happens.
    if (field === 'scheduledDate' && !(await confirmMove(row, value))) return false
    setDateSavingKey(`${row.id}:${field}`)
    setDateError(null)
    try {
      await proceduresApi.update(row.id, { [field]: value })
      await refreshPage()
      // Setting a SCHEDULE is the moment the operator has decided when the work
      // happens, so it is the moment to offer to book the run's equipment.
      // Clearing it, or changing the due date, offers nothing — neither says
      // anything new about when the machines are wanted.
      if (field === 'scheduledDate' && value) void openSchedulePlan(row, value)
      return true
    } catch (e) {
      setDateError(apiErrorMessage(e, 'Failed to update the date.'))
      return false
    } finally {
      setDateSavingKey(null)
    }
  }

  // ── Reserve the run from its schedule ──────────────────────────────────────

  const [planFor, setPlanFor] = useState<ProcedureRow | null>(null)
  const [planStartsAt, setPlanStartsAt] = useState('')
  const [plan, setPlan] = useState<ReservationPlan | null>(null)
  const [planLoading, setPlanLoading] = useState(false)
  const [planBusy, setPlanBusy] = useState(false)
  const [planError, setPlanError] = useState<string | null>(null)

  /**
   * Fetch the plan and open the dialog.
   *
   * Deliberately does NOT block the date save: the schedule is already
   * committed by the time this runs, so a failure here costs the offer to
   * reserve, not the operator's edit.
   */
  async function openSchedulePlan(row: ProcedureRow, startsAt: string) {
    setPlanFor(row)
    setPlanStartsAt(startsAt)
    setPlan(null)
    setPlanError(null)
    setPlanLoading(true)
    try {
      setPlan(await proceduresApi.reservationPlan(row.id, startsAt))
    } catch (e) {
      setPlanError(apiErrorMessage(e, 'Could not work out what this run needs.'))
    } finally {
      setPlanLoading(false)
    }
  }

  async function confirmSchedulePlan() {
    if (!planFor) return
    setPlanBusy(true)
    setPlanError(null)
    try {
      const res = await proceduresApi.reserveForSchedule(planFor.id, planStartsAt)
      // BEFORE the partial-result branch below, not after it. A partial booking
      // still created real holds, and returning early without this is what
      // leaves the step cards and the equipment calendar claiming nothing is
      // reserved until someone reloads.
      if (reservedAnything(res)) invalidateRunReservations()
      // Partial by design: kept on screen with the reasons; the operator closes it.
      const outcomeError = reserveOutcomeError(res)
      if (outcomeError) {
        setPlanError(outcomeError)
        setPlanBusy(false)
        return
      }
      setPlanFor(null)
    } catch (e) {
      setPlanError(apiErrorMessage(e, 'Could not reserve the equipment.'))
    } finally {
      setPlanBusy(false)
    }
  }

  // ── Work order ─────────────────────────────────────────────────────────────

  const [workOrderSavingId, setWorkOrderSavingId] = useState<string | null>(null)
  const [workOrderError, setWorkOrderError] = useState<string | null>(null)

  async function saveWorkOrder(row: ProcedureRow, value: string | null): Promise<boolean> {
    setWorkOrderSavingId(row.id)
    setWorkOrderError(null)
    try {
      await proceduresApi.update(row.id, { workOrderNumber: value })
      await refreshPage()
      return true
    } catch (e) {
      // Surfaced rather than swallowed: the common failure is a 409 naming the
      // procedure that already holds the number, which the operator must resolve.
      setWorkOrderError(apiErrorMessage(e, 'Failed to update the work order.'))
      return false
    } finally {
      setWorkOrderSavingId(null)
    }
  }

  // ── Quantity ───────────────────────────────────────────────────────────────

  const [quantitySavingId, setQuantitySavingId] = useState<string | null>(null)
  const [quantityError, setQuantityError] = useState<string | null>(null)

  async function saveQuantity(row: ProcedureRow, value: number): Promise<boolean> {
    setQuantitySavingId(row.id)
    setQuantityError(null)
    try {
      await proceduresApi.update(row.id, { quantity: value })
      // Refreshes the whole page, not just this cell: quantity multiplies the
      // steps' inventory requirements, so the expanded rows and any open pick
      // list are stale the moment it changes.
      await refreshPage()
      return true
    } catch (e) {
      // The expected failure is a 409 explaining that inventory has already been
      // allocated. That is the sentence the operator needs, so it is shown as-is.
      setQuantityError(apiErrorMessage(e, 'Failed to update the quantity.'))
      return false
    } finally {
      setQuantitySavingId(null)
    }
  }

  async function saveStepAssign(row: ProcedureRow, step: ProcedureJourneyStep, assigneeId: string | null) {
    if ((step.assignee?.id ?? null) === assigneeId) return
    setAssignSavingKey(`${row.id}:${step.stepId}`)
    setAssignError(null)
    try {
      await assignStep.mutateAsync({ id: row.id, stepId: step.stepId, assigneeId })
      await Promise.all([refreshPage(), loadProcedureJourney(row)])
      invalidateStepExecutions()
    } catch (e) {
      setAssignError(e instanceof Error ? e.message : 'Failed to assign the form.')
    } finally {
      setAssignSavingKey(null)
    }
  }

  // Patient name per MRN, as the server derived it from anchored form values
  // (z8nrz7e8kw). The rows carry their own; this is for the dropdown, and for
  // a row from an older backend that sends none.
  const nameByMrn = useMemo(
    () => new Map(patientOptions.map(o => [o.mrn, o.name] as const)),
    [patientOptions],
  )

  // Kit rows, only on the Kits page. Both feeds, because between them they cover
  // every kit run: `forms` is the in-progress ones and `inventory` the finished,
  // and a row that has just been completed must not fall out of the table while
  // one of the two caches is stale.
  const kitFormsQuery = useKitForms(isKit)
  const kitInventoryQuery = useKitInventory(isKit)
  // One row per KIT, several per run (z8nrz7e11d): rolled up to the run.
  const kitByProcedureId = useMemo(
    () => kitProgressByProcedure([...(kitFormsQuery.data ?? []), ...(kitInventoryQuery.data ?? [])]),
    [kitFormsQuery.data, kitInventoryQuery.data],
  )

  // Distinct patients for the "filter by patient" dropdown.
  const patients = useMemo(() => {
    const set = new Map<string, string>() // mrn -> display label
    // Runs with no patient are already left out: they offer nothing to filter
    // BY and would add a blank entry to the dropdown (z8nrz7ckj9).
    for (const { mrn, name } of patientOptions) {
      set.set(mrn, name ? `${name} (${mrn})` : mrn)
    }
    return Array.from(set.entries()).sort((a, b) => a[1].localeCompare(b[1]))
  }, [patientOptions])

  const templateVersionById = useMemo(() => {
    const map = new Map<string, number>()
    for (const t of procedureTemplates) map.set(t.id, t.version ?? 1)
    return map
  }, [procedureTemplates])

  const procedureRows = useMemo<ProcedureRow[]>(() => {
    /** Everything about a row that does not depend on how progress is derived. */
    const baseRow = (t: (typeof pagedProcedures)[number]) => ({
      id: t.id,
      mrn: t.patientMrn,
      mfgNumber: t.mfgNumber,
      capaRecords: t.capaRecords,
      patientName:
        t.patientName !== undefined
          ? t.patientName
          : t.patientMrn
            ? (nameByMrn.get(t.patientMrn) ?? null)
            : null,
      procedureTemplateId: t.procedureTemplateId,
      procedureTemplateName: t.procedureTemplate.name,
      procedureTemplateType: t.procedureTemplate.type ?? 'Transplant',
      // The run's own version when the server sends it; the offerable list only
      // knows each family's current one.
      procedureTemplateVersion:
        t.procedureTemplate.version ?? templateVersionById.get(t.procedureTemplateId) ?? 1,
      workOrderNumber: t.workOrderNumber ?? null,
      customerName: t.customer?.name ?? null,
      mesProduct: t.mesProduct ?? null,
      quantity: t.quantity ?? 1,
      status: t.status,
      startedAt: t.startedAt,
      // Server-set and absent on a non-product match, so it passes straight
      // through — no default, because absence is meaningful here (AC2.3).
      productMatches: t.productMatches,
      scheduledDate: t.scheduledDate,
      dueDate: t.dueDate,
      // Absent on an older backend reads as derived, which only costs a missing
      // Reset link on a date that was in fact typed.
      dueDateOverridden: t.dueDateOverridden ?? false,
      // Absent (an older backend) reads as "unknown", and unknown shows the
      // control: losing an affordance on a version skew is worse than showing
      // one that resolves to an empty sheet. Only an explicit false hides it.
      hasProcessStep: t.hasProcessStep !== false,
      assignedToLabel: assignedToLabel(t.assignedTo),
      procedureAssignee: t.assignedTo?.kind === 'user' ? t.assignedTo.user : null,
    })

    return pagedProcedures.map(t => {
      const j = journeyByKey.get(journeyKey(t.patientMrn, t.procedureTemplateId, t.id))
      const steps = j?.steps ?? []
      // A KIT's progress and status come from its BUILD, never from the patient
      // journey above.
      //
      // The journey is fetched per (patientMrn, procedureTemplateId), and a kit
      // has no patient — `createKitProcedure` stamps the kit's own supply id
      // into `patientMrn` so the column has something to show. No Patient row
      // has that MRN, so the journey resolves to no steps, `total` falls to 0,
      // and the branch below drops through to the procedure's own `status`.
      //
      // That status is the real trap: nothing advances a kit procedure past
      // `in_progress`. The kit's completion is recorded on the KitBuild — the
      // service says so outright, "the kit build, not form completion, decides
      // whether a kit is finished stock" — so a fully kitted and signed run read
      // "In progress" with a 0/0 bar forever, while Inventory → Kits → Kit
      // Inventory listed the very same kit as finished. Two screens, one
      // question, opposite answers.
      // The derivation (kit build first, then steps, then the column status) is
      // shared with the Overall Status board. See deriveProcedureStepStatus.
      // Repeat-aware: a 3x step counts as three, matching the step rail and the
      // patient-history panel. Identical to the old count for a run with no
      // repeating steps.
      //
      // From the row itself when the server sent it (z8nrz7e8kw), counted the
      // same way; from the journey otherwise.
      const progress = t.progress ?? {
        ...journeyProgress(steps),
        hasActive: steps.some(s => s.status === 'in_progress'),
      }
      return {
        ...baseRow(t),
        ...deriveProcedureStepStatus({
          procedureStatus: t.status,
          completed: progress.completed,
          total: progress.total,
          hasActive: progress.hasActive,
          hasPendingReview: t.hasPendingReview ?? false,
          kit: kitByProcedureId.get(t.id),
        }),
      }
    })
  }, [pagedProcedures, journeyByKey, nameByMrn, kitByProcedureId, templateVersionById])

  /**
   * One row per CAPA on the CAPA page; one row per procedure everywhere else.
   *
   * A CAPA is a capa-category form raised inside a run, so a run holding two of
   * them is two rows sharing every procedure-level column and differing in Event
   * ID and Form. Every variant goes through the same call so the table has one
   * row shape: with no records to fan out over, a row keeps its own id as its key
   * and carries no CAPA.
   */
  const rows = useMemo(
    () => expandCapaRows(procedureRows, row => (isCapa ? row.capaRecords : undefined)),
    [procedureRows, isCapa],
  )

  /**
   * What the four stat boxes count.
   *
   * Built from the rows the table is already showing, not a second request, so
   * the figures can never disagree with the list underneath — including while
   * a filter is applied, which is the useful behaviour: filter to one patient
   * and the boxes describe that patient.
   *
   * Rows with no CAPA (the version-skew case `expandCapaRows` guards) drop out
   * here rather than being counted as an untyped, statusless one.
   */
  const capaStatRows = useMemo<CapaStatInput[]>(
    () =>
      isCapa
        ? rows.flatMap(row =>
            row.capa
              ? [{
                  capa: row.capa,
                  dueDate: row.dueDate,
                  type: row.procedureTemplateType ?? null,
                }]
              : [],
          )
        : [],
    [rows, isCapa],
  )

  // Patient options for the create-procedure typeahead — sourced exclusively
  // from the Patient table so this list matches what the patients page shows.
  const allPatientOptions = useMemo(() => {
    return allPatients
      .map(p => {
        const name = p.fullName ?? ([p.firstName, p.lastName].filter(Boolean).join(' ') || null)
        return { mrn: p.mrn, label: name ? `${name} (${p.mrn})` : p.mrn }
      })
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [allPatients])

  const filteredPatientOptions = useMemo(() => {
    const q = patientSearch.trim().toLowerCase()
    if (!q) return allPatientOptions
    return allPatientOptions.filter(
      p => p.mrn.toLowerCase().includes(q) || p.label.toLowerCase().includes(q),
    )
  }, [allPatientOptions, patientSearch])

  // Each list creates its own kind of run (type selection was removed from the
  // dialog — it's implicit in the template the user picks): Kits kit templates,
  // MES MES templates, Transplants transplant templates only.
  const createTypeTemplates = useMemo(
    () => templatesOfferedFor(variant, searchedTemplates),
    [searchedTemplates, variant],
  )

  // Derive the procedure type from whichever template is selected.
  const selectedTemplate = searchedTemplates.find(p => p.id === selectedProcedureTemplateId)
    ?? procedureTemplates.find(p => p.id === selectedProcedureTemplateId)
  const createProcedureType: ProcedureType = isKit
    ? KIT_TYPE
    : isMes
      ? MES_TYPE
      : (selectedTemplate?.type ?? 'Transplant')
  /**
   * Where the current refusal is actually rendered.
   *
   * Two of the inputs come and go — Patient is hidden for a KIT or MES run,
   * which have no patient, and Due in disappears once an explicit due date is
   * typed. A message pinned to one of those would render inside a hidden
   * container and be lost, so it falls back to the dialog-level banner. A
   * refusal is never shown in both places, and never in neither.
   */
  const shownErrorField: ProcedureCreateField | null =
    (createErrorField === 'patient' && (isKit || isMes)) ||
    (createErrorField === 'dueOffset' && !!createDueDate)
      ? null
      : createErrorField
  // Shown as the offset field's placeholder, so "blank" states what it will
  // actually do rather than looking unset. `?? null` and not `|| null` — a
  // template offset of 0 is real and must not read as "no default".
  const templateDueOffset = selectedTemplate?.dueOffsetDays ?? null

  const createIsSubmittable = canSubmitProcedure({
    procedureType: createProcedureType,
    procedureTemplateId: selectedProcedureTemplateId,
    patientMrn: selectedPatientMrn,
  })

  /*
   * Repeating steps of the template being created from, and what the operator
   * typed for each (z8nrz7dkkc).
   *
   * Kept as STRINGS keyed by stepId, like every other number field in this
   * dialog: a half-typed box is a legitimate intermediate state, and parsing on
   * every keystroke would fight the user for the cursor.
   *
   * Only steps whose `repeatCount` is non-null appear — the server stores a
   * count solely for a form carrying the Repeat Form action, so a non-null
   * count IS the signal that the form repeats. No second lookup of
   * `allowedActions` is needed, and none could disagree with this one.
   */
  const [repeatSteps, setRepeatSteps] = useState<ProcedureTemplateSequenceStep[]>([])
  const [createRepeats, setCreateRepeats] = useState<Record<string, string>>({})

  /*
   * Load the chosen template's repeating steps so the dialog can ask for a
   * quantity per form.
   *
   * Fetched on SELECTION rather than with the template list: the list is a
   * search over every offerable template and carries no steps, and pulling the
   * full sequence of each just to learn which repeat would be a request per row
   * for a question about one.
   */
  useEffect(() => {
    if (!selectedProcedureTemplateId) {
      setRepeatSteps([])
      setCreateRepeats({})
      return
    }
    let cancelled = false
    procedureTemplatesApi
      .get(selectedProcedureTemplateId)
      .then(detail => {
        if (cancelled) return
        setRepeatSteps(detail.steps.filter(s => s.repeatCount != null))
        // Left empty: an untouched row falls back to the template's number at
        // render AND at submit, so the run only pins a count someone chose.
        setCreateRepeats({})
      })
      // A template whose sequence cannot be read still creates fine — it just
      // uses its own counts. Failing the whole dialog over this would block a
      // creation that has nothing to do with repeats.
      .catch(() => {
        if (!cancelled) setRepeatSteps([])
      })
    return () => {
      cancelled = true
    }
  }, [selectedProcedureTemplateId])

  const [createCapaOpen, setCreateCapaOpen] = useState(false)

  function openCreateProcedure() {
    // The CAPA page raises a CAPA on an existing run — a form attached to a
    // procedure already in progress, exactly what the "+" menu on a procedure
    // page does. There is no procedure to create here, so it never opens the
    // template dialog below.
    if (isCapa) {
      setCreateCapaOpen(true)
      return
    }
    setPatientSearch('')
    setSelectedPatientMrn('')
    setSelectedProcedureTemplateId('')
    setTemplateSearch('')
    setDebouncedTemplateSearch('')
    setTemplateDropdownOpen(false)
    // A kit run is normally raised on the day it is started, so the Kits page
    // prefills today rather than making the operator reach for the picker.
    // Still editable, and still optional for patient care, where the planned
    // start is a real scheduling decision rather than "now".
    //
    // In the input's own `YYYY-MM-DDTHH:mm` form. A bare `YYYY-MM-DD` renders
    // as an empty datetime-local box AND sorts before today's `T00:00` floor,
    // so every kit created with the default was refused as "in the past".
    setCreateScheduledDate(isKit ? toLocalInput(new Date().toISOString()) : '')
    setCreateDueDate('')
    // Blank, not the template's number: an empty field already MEANS "use the
    // template's", and pre-filling it would turn every run into an explicit
    // override that stops following the template if it is ever retuned.
    setCreateDueOffset('')
    setCreateQuantity('1')
    setCreateWorkOrder('')
    setCreateCustomerId('')
    setCreateMesProductId('')
    setRepeatSteps([])
    setCreateRepeats({})
    clearCreateError()
    setPatientDropdownOpen(false)
    setCreateOpen(true)
    // Kit runs and MES orders have no patient to pick.
    if (!isKit && !isMes) loadCreatePatients()
  }

  async function submitCreateProcedure() {
    const creatingKit = createProcedureType === KIT_TYPE
    if (!createIsSubmittable) {
      failCreate(
        creatingKit ? 'Select a KIT procedure.' : 'Select both a patient and a procedure.',
        creatingKit ? 'template' : 'patient',
      )
      return
    }
    // String compare is safe and stays correct with a time: `datetime-local`
    // values are fixed-width `YYYY-MM-DDTHH:mm`, so lexical order is
    // chronological order. The picker's `min` already makes this hard to hit;
    // it remains for a value typed rather than picked.
    if (createScheduledDate && createDueDate && createDueDate < createScheduledDate) {
      failCreate('The due date cannot be earlier than the scheduled start.', 'dueDate')
      return
    }
    // Same reason as above: the picker greys these days out, the keyboard does
    // not. Says which bound was crossed rather than a generic "invalid date".
    if (createScheduledDate && createScheduledDate < todayStart) {
      failCreate('The scheduled start cannot be in the past.', 'scheduledDate')
      return
    }
    if (createDueDate && createDueDate < todayStart) {
      failCreate('The due date cannot be in the past.', 'dueDate')
      return
    }
    // Same reason as the dates: `min`/`max` bind the spinner, not the keyboard.
    const offsetRaw = createDueOffset.trim()
    if (offsetRaw !== '') {
      const n = Number(offsetRaw)
      if (!Number.isInteger(n) || n < 0 || n > MAX_DUE_OFFSET_DAYS) {
        failCreate(
          `Due in must be a whole number of days between 0 and ${MAX_DUE_OFFSET_DAYS}.`,
          'dueOffset',
        )
        return
      }
    }
    if (!isValidProcedureQuantity(createQuantity)) {
      failCreate(
        `Quantity must be a whole number between 1 and ${MAX_PROCEDURE_QUANTITY}.`,
        'quantity',
      )
      return
    }
    if (!isValidWorkOrderNumber(createWorkOrder)) {
      failCreate(WORK_ORDER_FORMAT_MESSAGE, 'workOrder')
      return
    }
    // Resolved once and sent on BOTH branches. The kit branch is exactly where
    // a create-form field gets forgotten (see canSubmitProcedure's comment),
    // and it is also the branch quantity matters most on.
    const quantity = procedureQuantityPayload(createQuantity)
    const workOrderNumber = workOrderPayload(createWorkOrder)
    // Only where the field is shown — a value can never leak onto a type the
    // server would refuse it on.
    const customerId = copy.customer ? createCustomerId : ''
    setCreating(true)
    clearCreateError()
    try {
      if (creatingKit) {
        // No patient, and no assignee: "creating the procedure there is no
        // assignment — to execute the KIT you then assign."
        const build = await createKitProcedure.mutateAsync({
          procedureTemplateId: selectedProcedureTemplateId,
          ...(quantity ? { quantity } : {}),
          ...(createScheduledDate ? { scheduledDate: fromLocalInput(createScheduledDate)! } : {}),
          ...(workOrderNumber ? { workOrderNumber } : {}),
          ...(customerId ? { customerId } : {}),
        })
        setCreateOpen(false)
        navigate(`/kits/runs/${build.procedureId}`)
        return
      }
      const created = await proceduresApi.create({
        // Omitted, not sent empty: an MES order has no patient at creation
        // (z8nrz7ckj9). The key still has to be absent rather than `''` — the
        // format rule that used to reject an empty string is gone, but
        // `@IsNotEmpty` replaced it (z8nrz7dyc2), so a supplied blank is still a
        // 400 rather than reading as "no patient".
        ...(selectedPatientMrn ? { patientMrn: selectedPatientMrn } : {}),
        procedureTemplateId: selectedProcedureTemplateId,
        // Instants, not the picker's local strings — the columns are timestamps
        // and would otherwise be read as UTC, shifting every value by the
        // operator's offset.
        ...(createScheduledDate ? { scheduledDate: fromLocalInput(createScheduledDate)! } : {}),
        ...(createDueDate ? { dueDate: fromLocalInput(createDueDate)! } : {}),
        // Blank means "use the template's", so it is OMITTED rather than sent
        // as null. `!== ''` and not a truthiness check — '0' is a real offset
        // (due the same day) that a truthy test would drop.
        ...(createDueOffset.trim() !== '' ? { dueOffsetDays: Number(createDueOffset) } : {}),
        ...(quantity ? { quantity } : {}),
        ...(workOrderNumber ? { workOrderNumber } : {}),
        ...(customerId ? { customerId } : {}),
        // Only where the field is shown, so it never reaches a type the server refuses.
        ...(canPickProduct && createMesProductId ? { mesProductId: createMesProductId } : {}),
      })

      /*
       * Pin the per-form repeat counts the operator chose.
       *
       * After the create, not part of it: a repeat count is an override on a
       * run that must already exist, and folding it into the create payload
       * would need the server to resolve step ids mid-creation for a value most
       * runs never set.
       *
       * Only rows actually changed are sent — an untouched form keeps following
       * its template, which is what lets a retuned template still reach runs
       * nobody customised. Failures here do NOT fail the creation: the run
       * exists and is usable at its template's counts, and the quantities stay
       * editable from the row.
       */
      const pinned = repeatSteps
        .map(step => {
          const typed = createRepeats[step.stepId]
          if (typed === undefined || typed.trim() === '') return null
          const next = Number.parseInt(typed, 10)
          if (Number.isNaN(next) || next < 1 || next === (step.repeatCount ?? 1)) return null
          return { stepId: step.stepId, next }
        })
        .filter((r): r is { stepId: string; next: number } => r != null)

      if (pinned.length > 0) {
        await Promise.allSettled(
          pinned.map(r => proceduresApi.setStepRepeats(created.id, r.stepId, r.next)),
        )
      }

      setCreateOpen(false)
      // Land on the new record's OWN detail route. This used to send anything
      // without a patient to /mes/orders/<id>, which was fine while MES was the
      // only patient-less type and wrong the moment CAPA arrived — a new CAPA
      // opened as an MES order, complete with a patient breadcrumb.
      navigate(
        procedureRunPath(createProcedureType, created.id, selectedPatientMrn || null) ??
          `/procedures`,
      )
    } catch (e) {
      // `proceduresApi` throws the raw response text, so `e.message` here IS the
      // Nest envelope — this line used to print
      // `{"message":[…],"error":"Bad Request","statusCode":400}` at the operator
      // (z8nrz7dyc2). `apiErrorMessage` unwraps it; `procedureCreateErrorField`
      // decides which input it belongs beside, and returns null when it cannot
      // tell, which leaves the message in the banner rather than hiding it.
      const message = apiErrorMessage(e, `Failed to create ${copy.one}.`)
      failCreate(message, procedureCreateErrorField(message))
    } finally {
      setCreating(false)
    }
  }

  /**
   * Open the run a row is about.
   *
   * Routed by the run's OWN kind rather than by this page's variant, because the
   * CAPA page lists CAPAs raised inside runs of every kind — the host of a CAPA
   * row can be a transplant, a kit build or an MES order, and only the row knows
   * which. See `procedureRunPath`.
   */
  function openProcedure(row: RenderedRow) {
    const path = procedureRunPath(row.procedureTemplateType, row.id, row.mrn)
    // Patient care with no patient has no URL at all; nothing to open.
    if (!path) return
    // A CAPA row IS one form of its run, so open the run ON that form rather
    // than on whichever step happens to be actionable.
    const target = row.capa ? withQuery(path, adHocStepQuery(row.capa.id)) : path
    // A back link is worth having wherever the row came from a list the run's
    // own page does not know about — which is every CAPA row, and patient care
    // reached from this table.
    navigate(row.capa || path.startsWith('/patients/') ? withOrigin(target) : target)
  }


  // Cancel procedure dialog state (z8nrz7f7g0). A procedure is never deleted:
  // cancelling closes it and voids its open forms, and every form and audit
  // trail is kept. The reason is recorded on the run and on each voided form.
  const [deleteTarget, setDeleteTarget] = useState<ProcedureRow | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [cancelReason, setCancelReason] = useState('')

  async function confirmDelete(row: ProcedureRow) {
    setDeleting(true)
    setDeleteError(null)
    try {
      await proceduresApi.remove(row.id, cancelReason.trim())
      setDeleteTarget(null)
      reloadAll()
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'Failed to cancel the procedure.')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className='min-h-full bg-muted/20'>
      <div className='px-6 py-8'>

        <div className='mb-6 flex items-center justify-between gap-4'>
          {/* Title, icon and description on one line (z8nrz7e46x). The CAPA page
              is a lens over the others, so it has no status icon of its own. */}
          <PageHeading
            title={pageTitle}
            icon={
              !isCapa && (
                <OverallStatusLink
                  type={isKit ? 'KitCreation' : isMes ? 'MES' : 'Transplant'}
                  placement='title'
                />
              )
            }
            description={copy.blurb}
          />
          <div className='flex items-center gap-2'>
            {/* One create flow for kits and patient care. A kit is "the same
                process except the KIT is pre-selected" — so this opens the same
                dialog, with the type already set and the patient field gone.

                CAPA is the exception, and deliberately: a CAPA is not a
                procedure to create but a capa-category FORM raised inside a run
                already in progress. So on that page this opens
                CreateCapaDialog — pick the run, pick the form — which is the
                same act as the "+" menu on a procedure page, for someone who
                started from the CAPA list instead. */}
            <Button size='sm' onClick={openCreateProcedure}>
              <Plus className='mr-2 h-4 w-4' />
              {copy.create}
            </Button>
            <Button variant='ghost' size='sm' onClick={reloadAll} disabled={loading}>
              {loading ? <Loader2 className='h-4 w-4 animate-spin' /> : 'Refresh'}
            </Button>
          </div>
        </div>

        {isCapa && <CapaStatCards rows={capaStatRows} loading={pageLoading} />}

        {/* Filters */}
        <div className='mb-5 flex flex-wrap items-center gap-2'>
          <div className='relative min-w-[16rem] flex-1'>
            <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
            <Input
              className='pl-9'
              placeholder={copy.search}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          {/* Neither a kit nor an MES order has a patient to filter by. */}
          {!isKit && !isMes && (
          <Select value={patientFilter} onValueChange={setPatientFilter}>
            <SelectTrigger className='w-64'>
              <SelectValue placeholder='Filter by patient' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All patients</SelectItem>
              {patients.map(([mrn, label]) => (
                <SelectItem key={mrn} value={mrn}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          )}

          {/* Kits and MES: whose runs (z8nrz7e71k). Only there — only those
              runs carry a customer — and only for someone who can read the list. */}
          {canPickCustomer && (
            <Select value={customerFilter} onValueChange={setCustomerFilter}>
              <SelectTrigger className='w-56' aria-label='Filter by customer'>
                <SelectValue placeholder='Customer' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>All customers</SelectItem>
                {customerOptions.map(c => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className='w-44' aria-label='Filter by status'>
              <SelectValue placeholder='Status' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>Any status</SelectItem>
              {/* The real enum, not a display vocabulary of its own: the server
                  400s anything else, and a label that is not a value is how a
                  filter silently stops matching. */}
              <SelectItem value='in_progress'>In progress</SelectItem>
              {/* Derived server-side from the run's forms (z8nrz7dt41). */}
              <SelectItem value='pending_review'>Pending review</SelectItem>
              <SelectItem value='completed'>Completed</SelectItem>
              <SelectItem value='cancelled'>Cancelled</SelectItem>
            </SelectContent>
          </Select>

          {/* Assignment is per STEP, so this reads "has a step assigned to
              them", which is also what the Assigned To column derives from. */}
          <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
            <SelectTrigger className='w-52' aria-label='Filter by assignee'>
              <SelectValue placeholder='Assigned to' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>Anyone assigned</SelectItem>
              {assignableUsers.map(u => (
                <SelectItem key={u.id} value={u.id}>
                  {u.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Raised BETWEEN — on createdAt, not the scheduled date, which is
              nullable and would drop every unscheduled run from the range. */}
          <div className='flex items-center gap-1.5'>
            <label className='text-xs text-muted-foreground' htmlFor='procedures-created-from'>
              Raised
            </label>
            <Input
              id='procedures-created-from'
              type='date'
              value={createdFrom}
              max={createdTo || undefined}
              onChange={e => setCreatedFrom(e.target.value)}
              className='w-[9.5rem]'
              aria-label='Raised on or after'
            />
            <span className='text-xs text-muted-foreground'>to</span>
            <Input
              type='date'
              value={createdTo}
              min={createdFrom || undefined}
              onChange={e => setCreatedTo(e.target.value)}
              className='w-[9.5rem]'
              aria-label='Raised on or before'
            />
          </div>

          {hasActiveFilters && (
            <Button variant='ghost' size='sm' onClick={clearFilters}>
              Clear filters
            </Button>
          )}

        </div>

        {/* What is currently narrowing the list, each individually removable.
            A filter you cannot see is a filter you cannot explain an empty
            table with. */}
        {hasActiveFilters && (
          <div className='mb-4 flex flex-wrap items-center gap-2 text-xs'>
            {activeFilters.map(f => (
              <span
                key={f.key}
                className='inline-flex items-center gap-1.5 rounded-full border bg-muted/40 px-2.5 py-1'
              >
                <span className='text-muted-foreground'>{f.label}:</span>
                <span className='font-medium'>{f.value}</span>
                <button
                  type='button'
                  onClick={() => clearFilter(f.key)}
                  aria-label={`Clear ${f.label} filter`}
                  className='rounded-full px-1 text-muted-foreground hover:bg-muted hover:text-foreground'
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}

        {error && (
          <div className='mb-4 flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive'>
            <AlertCircle className='h-4 w-4 shrink-0' />
            {error}
          </div>
        )}

        {assignError && (
          <div className='mb-4 flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive'>
            <AlertCircle className='h-4 w-4 shrink-0' />
            {assignError}
          </div>
        )}

        {dateError && (
          <div className='mb-4 flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive'>
            <AlertCircle className='h-4 w-4 shrink-0' />
            {dateError}
          </div>
        )}

        {workOrderError && (
          <div className='mb-4 flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive'>
            <AlertCircle className='h-4 w-4 shrink-0' />
            {workOrderError}
          </div>
        )}

        {quantityError && (
          <div className='mb-4 flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive'>
            <AlertCircle className='h-4 w-4 shrink-0' />
            {quantityError}
          </div>
        )}

        {pageLoading && (
          <div className='flex items-center justify-center py-24'>
            <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
          </div>
        )}

        {!pageLoading && pagedTotal === 0 && (
          <div className='rounded-xl border border-dashed bg-card py-16 text-center'>
            <HeartPulse className='mx-auto mb-3 h-10 w-10 text-muted-foreground/30' />
            <p className='text-sm text-muted-foreground'>
              {/* "Nothing matched" and "nothing exists" are different facts,
                  and only one of them is fixed by clearing a filter. */}
              {search.trim() || patientFilter !== 'all' || hasActiveFilters
                ? `No ${pageNoun} match these filters.`
                : `No ${pageNoun} yet.`}
            </p>
          </div>
        )}

        {!pageLoading && rows.length > 0 && (
          // One provider for the whole table rather than one per row: the
          // Created By cell tooltips an address on every CAPA row.
          <TooltipProvider delayDuration={200}>
            {/* `overflow-x-persistent`, not `overflow-x-auto`: on macOS the
                platform's OVERLAY scrollbar appears only while a scroll is in
                progress, so a table wider than its container reads as one that
                has simply been cut off. See the rule in index.css. */}
            <div className='overflow-x-persistent rounded-xl border bg-card'>
            {/*
              NOTHING IN A ROW WRAPS, and the table takes whatever width that
              needs — `w-full` is a floor, not a cap.

              This is the fix for the CAPA page rendering "multiple lines for a
              given column". With 14 columns the content is wider than any
              screen, and `table-layout: auto` responds by shrinking each column
              towards its MIN-CONTENT width — which for text is its longest
              WORD. So "capa form - transplant" broke over three lines while an
              email address, having no spaces to break at, kept its full width
              and took the space from its neighbours.

              Refusing to wrap makes each column's min-content its whole line,
              so the table overflows and scrolls instead of stacking. The two
              genuinely unbounded columns cap themselves with `truncate` below;
              everything else is short by nature.

              Scoped to the row, so the expanded forms panel underneath still
              wraps normally.
            */}
            <table className='w-full text-sm'>
              <thead className='whitespace-nowrap border-b bg-table-header text-left text-xs text-table-header-foreground'>
                {/* Every header comes from `cols`, so the CAPA-only columns
                    (Form, Created By) and the variant-dependent leading header
                    are decided in proceduresColumns rather than inline here.

                    On the leading column: there is no Patient column on CAPA —
                    a CAPA is raised against a run (a transplant, a KIT ID, an
                    MES work order), never against a person. The patient stays
                    SEARCHABLE (the box matches name and MRN), which is how a
                    given patient's CAPA work is reached without the column.
                    What leads instead is the CAPA's own EVENT ID, minted from an
                    internal count: E_ID1, E_ID2, E_ID323. Two CAPAs raised from
                    one template were otherwise indistinguishable here, and a
                    quality record that cannot be cited is not much of a record.

                    "Procedure", not "Template", on every variant: it shows the
                    template's name and version, but what a reader identifies is
                    the procedure in front of them. Form names which CAPA form
                    was picked when the CAPA was raised, and Created By its
                    author — kept distinct from whoever it is assigned to now. */}
                <tr>
                  {cols.visible.map(({ def }) => (
                    <ResizableTh
                      key={def.id}
                      columnId={def.id}
                      columns={cols}
                      label={def.header}
                      className={PROCEDURE_HEAD_CLASSES[def.id] ?? PROCEDURE_HEAD_DEFAULT}
                      resizable={def.id !== 'expand' && def.id !== 'actions'}
                    >
                      {def.id === 'expand' ? null : def.header}
                    </ResizableTh>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const meta = STATUS_META[row.stepStatus]
                  const pct = row.total > 0 ? Math.round((row.completed / row.total) * 100) : 0
                  const expanded = expandedId === row.rowKey
                  const procedureJourney = procedureJourneys.get(row.id)
                  return (
                    <Fragment key={row.rowKey}>
                    <tr
                      role='link'
                      tabIndex={0}
                      aria-label={`Open ${copy.one} ${row.patientName ?? row.mrn ?? row.mfgNumber ?? ''}`.trim()}
                      onClick={() => openProcedure(row)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          openProcedure(row)
                        }
                      }}
                      className={cn(
                        'cursor-pointer whitespace-nowrap border-b transition-colors last:border-0 hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                        i % 2 === 1 && 'bg-muted/10',
                      )}
                    >
                      <ColumnCells
                        columns={cols}
                        cells={{
                          expand: (
                            <td className='px-2 py-3'>
                              <button
                                type='button'
                                title={expanded ? 'Hide forms' : 'Show forms'}
                                onClick={e => {
                                  e.stopPropagation() // row click opens the procedure
                                  toggleExpand(row)
                                }}
                                className='flex h-6 w-6 items-center justify-center rounded-md hover:bg-muted'
                              >
                                {expanded ? (
                                  <ChevronDown className='h-4 w-4 text-muted-foreground' />
                                ) : (
                                  <ChevronRight className='h-4 w-4 text-muted-foreground' />
                                )}
                              </button>
                            </td>
                          ),
                          lead: (
                            <td className='px-4 py-3'>
                              {/* Neither a kit nor an MES order has a patient here: the
                                  kit column carries its supply id, the MES column its
                                  minted MFG number. Both are ids, so neither looks for
                                  a person's name behind them. */}
                              <div className='flex items-center gap-1.5'>
                                <div className='min-w-0'>
                                  {isKit ? (
                                    <div className='font-mono text-xs font-semibold'>{row.mrn}</div>
                                  ) : isCapa ? (
                                    <div className='font-mono text-xs font-semibold'>
                                      {/* THIS row's CAPA, not the run's — a run holding
                                          two CAPAs renders two rows with two ids. Null
                                          only on one raised before Event IDs existed. */}
                                      {row.capa?.capaEventId ?? '—'}
                                    </div>
                                  ) : isMes ? (
                                    <div className='font-mono text-xs font-semibold'>
                                      {/* Null only on a run created before the number
                                          existed — blank is correct, not an error. */}
                                      {row.mfgNumber ?? '—'}
                                    </div>
                                  ) : (
                                    <>
                                      <div className='font-medium'>{row.patientName ?? row.mrn}</div>
                                      {row.patientName && (
                                        <div className='font-mono text-xs text-muted-foreground'>
                                          {row.mrn}
                                        </div>
                                      )}
                                      {/* Why this row is here, when a DIN found it
                                          (AC2.3). Anchored under the identifier rather
                                          than given a column of its own: Out of scope
                                          rules out a DIN column, and the annotation is
                                          about THIS search, not about the case. */}
                                      <ProductMatchNote matches={row.productMatches} />
                                    </>
                                  )}
                                </div>
                                {/* Beside the identifier, not in the trailing overflow
                                    cell — the mock anchors it to what it acts on.

                                    Only where a Process Step is found: inventory,
                                    equipment and staffing are configured ON a process
                                    field, so a template without one has no bill of
                                    materials and the menu could only ever produce an
                                    empty sheet. It rendered on every row regardless of
                                    type, which is what the client reported. */}
                                {/* LIS results waiting for approval on this run. The row
                                    click opens the run, where the review lives. */}
                                <LisPendingBadge scope={{ procedureId: row.id }} />
                                {row.hasProcessStep && (
                                  <PickListActionsMenu
                                    scope={{ scope: 'PROCEDURE', procedureId: row.id }}
                                    open={openMenuRowId === row.rowKey}
                                    onOpenChange={next => setOpenMenuRowId(next ? row.rowKey : null)}
                                  />
                                )}
                              </div>
                            </td>
                          ),
                          procedure: (
                            <td className='px-4 py-3'>
                              {/* On the CAPA page this is the run the CAPA was raised
                                  IN, which is also the run it is about — a CAPA lives
                                  inside the thing it concerns, so no second link is
                                  needed to say what it addresses. */}
                              {/* Capped and ellipsised rather than left to grow: a
                                  template name is author-typed and has no length
                                  limit, and one long one would otherwise set the
                                  width of the column on every page. The VERSION never
                                  truncates — it is what tells two rows of the same
                                  template apart — so only the name shrinks. */}
                              <div className='flex max-w-[20rem] items-baseline gap-1'>
                                <span className='min-w-0 truncate' title={row.procedureTemplateName}>
                                  {row.procedureTemplateName}
                                </span>
                                <span className='shrink-0 text-xs text-muted-foreground'>
                                  ({versionLabel(row.procedureTemplateVersion)})
                                </span>
                              </div>
                            </td>
                          ),
                          form: (
                              <td className='px-4 py-3'>
                                {/* Same reasoning: the form's name is author-typed.
                                    The full label is on hover. */}
                                <div className='max-w-[18rem] truncate' title={capaFormLabel(row.capa)}>
                                  {capaFormLabel(row.capa)}
                                </div>
                              </td>
                          ),
                          // Only in the MES column set (z8nrz7fvhx). The code opens
                          // the product, not the run, so the click stops here.
                          productCode: (
                            <td className='px-4 py-3'>
                              {row.mesProduct ? (
                                <button
                                  type='button'
                                  className='font-medium text-primary underline underline-offset-2'
                                  onClick={e => {
                                    e.stopPropagation()
                                    setProductInfo({ id: row.mesProduct!.id, mfgNumber: row.mfgNumber ?? null })
                                  }}
                                >
                                  {row.mesProduct.code}
                                </button>
                              ) : (
                                <span className='text-muted-foreground'>—</span>
                              )}
                            </td>
                          ),
                          productDescription: (
                            <td className='px-4 py-3'>
                              {(() => {
                                if (!row.mesProduct) return <span className='text-muted-foreground'>—</span>
                                const d = productDescriptions(row.mesProduct, primaryDescription)
                                return (
                                  <div className='max-w-[16rem] truncate' title={d.other || undefined}>
                                    {d.primary || <span className='text-muted-foreground'>—</span>}
                                  </div>
                                )
                              })()}
                            </td>
                          ),
                          // Only in the Kit and MES column sets (z8nrz7e714).
                          customer: (
                            <td className='px-4 py-3'>
                              <div className='max-w-[14rem] truncate' title={row.customerName ?? undefined}>
                                {row.customerName ?? <span className='text-muted-foreground'>—</span>}
                              </div>
                            </td>
                          ),
                          // Minted for a Transplant, and editable on any row: the
                          // number usually belongs to an outside system, so a blank
                          // is an invitation to type theirs rather than missing data.
                          workOrder: (
                            <WorkOrderCell
                              value={row.workOrderNumber}
                              canManage={canManage}
                              saving={workOrderSavingId === row.id}
                              onSave={next => saveWorkOrder(row, next)}
                            />
                          ),
                          quantity: (
                            <QuantityCell
                              value={row.quantity}
                              canManage={canManage}
                              saving={quantitySavingId === row.id}
                              onSave={next => saveQuantity(row, next)}
                            />
                          ),
                          progress: (
                            <td className='px-4 py-3'>
                              <div className='flex items-center gap-2'>
                                <div className='h-1.5 w-20 overflow-hidden rounded-full bg-slate-200'>
                                  <div
                                    className='h-full rounded-full bg-emerald-500'
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                                <span className='text-xs tabular-nums text-muted-foreground'>
                                  {row.completed}/{row.total}
                                </span>
                              </div>
                            </td>
                          ),
                          status: (
                            <td className='px-4 py-3'>
                              <span
                                className={cn(
                                  'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium',
                                  meta.badge,
                                )}
                              >
                                <span className={cn('h-1.5 w-1.5 rounded-full', meta.dot)} />
                                {row.stepLabel}
                              </span>
                            </td>
                          ),
                          createdBy: (
                            <CapaAuthorCell capa={row.capa} />
                          ),
                          assignedTo: (
                            <td
                              className='px-4 py-3'
                              onClick={e => canAssign && e.stopPropagation()}
                            >
                              {canAssign ? (
                                <AssigneeSelect
                                  users={assignableUsers}
                                  value={row.procedureAssignee}
                                  placeholder={row.assignedToLabel}
                                  disabled={assignSavingKey === row.id}
                                  onChange={assigneeId => saveProcedureAssign(row, assigneeId)}
                                  className='w-40'
                                  buttonClassName='h-7 px-2 text-xs'
                                />
                              ) : (
                                <span
                                  className={cn(
                                    'text-xs',
                                    row.assignedToLabel === 'Unassigned'
                                      ? 'text-muted-foreground'
                                      : 'font-medium',
                                  )}
                                >
                                  {row.assignedToLabel}
                                </span>
                              )}
                            </td>
                          ),
                          started: (
                            /*
                             * Absolute date, not "26d ago" (z8nrz7dkzy AC1.1).
                             *
                             * This cell is shared by all four variants of this
                             * table — patient (Transplants), kit, mes and capa
                             * all get `started` from proceduresColumns() — so
                             * the change lands on every procedure list, not
                             * only Transplants. That is wider than the
                             * ticket's Out of scope and was agreed explicitly:
                             * the arithmetic complaint applies just as much to
                             * a Kit run, and gating one variant onto a
                             * different date format would make the same column
                             * mean two things across pages.
                             */
                            <td className='px-4 py-3 text-xs text-muted-foreground'>
                              {formatStartedOn(row.startedAt)}
                            </td>
                          ),
                          scheduled: (
                            <DateCell
                              value={row.scheduledDate}
                              canManage={canManage}
                              // THIS cell's own save, not any date save anywhere in
                              // the table — the flag now drives a spinner on the
                              // apply button, so a shared one would spin every row.
                              saving={dateSavingKey === `${row.id}:scheduledDate`}
                              // A run cannot be planned into the past: earlier days
                              // are greyed out in the picker and refused on commit.
                              // Rows already holding an earlier date keep showing it
                              // — the bound applies to what you pick, not what is
                              // already recorded.
                              min={todayStart}
                              // NOT gated on the run having an owner any more, to
                              // match the server. A date books nothing and holds
                              // nothing, so an unassigned run carrying one strands no
                              // machine — and planning WHEN a run happens before
                              // deciding WHO does it is the normal order of work. The
                              // rule still applies where resources are actually
                              // committed: reserving requires an owner, and takes one
                              // on the reserver's behalf rather than refusing.
                              onSave={next => saveDate(row, 'scheduledDate', next)}
                            />
                          ),
                          due: (
                            <DateCell
                              value={row.dueDate}
                              canManage={canManage}
                              saving={dateSavingKey === `${row.id}:dueDate`}
                              overdue={isOverdue(row.dueDate, row.status, today)}
                              // Cannot be earlier than the run's own start — or, on a
                              // run with no start yet, earlier than today, so the two
                              // columns cannot disagree about whether the past is
                              // plannable.
                              min={row.scheduledDate ?? todayStart}
                              derived={!!row.dueDate && !row.dueDateOverridden}
                              // Clearing drops the override server-side, so the date
                              // starts following the schedule again.
                              onReset={() => void saveDate(row, 'dueDate', null)}
                              onSave={next => saveDate(row, 'dueDate', next)}
                            />
                          ),
                          actions: (
                            <td className='px-4 py-3'>
                              <div className='flex items-center justify-end gap-1'>
                                <Button
                                  size='sm'
                                  variant='ghost'
                                  title={row.status === 'cancelled' ? `This ${copy.one} is cancelled` : `Cancel ${copy.one}`}
                                  disabled={row.status === 'cancelled'}
                                  onClick={e => {
                                    e.stopPropagation() // row click opens the procedure
                                    setDeleteError(null)
                                    setCancelReason('')
                                    setDeleteTarget(row)
                                  }}
                                  // The row also handles Enter/Space; without this,
                                  // Space on the button navigates instead of cancelling.
                                  onKeyDown={e => e.stopPropagation()}
                                >
                                  <Ban className='h-3.5 w-3.5 text-destructive' />
                                </Button>
                                <ChevronRight className='h-4 w-4 text-muted-foreground/40' />
                              </div>
                            </td>
                          ),
                        }}
                      />
                    </tr>
                    {expanded && (
                      <tr className='border-b bg-muted/20 last:border-0'>
                        {/* Spans the whole header row: chevron + 9 columns + the
                            trailing actions cell. Bump this with any column. */}
                        {/* Two extra columns on the CAPA page (Form, Created By). */}
                        <td colSpan={cols.visible.length} className='pl-16 pr-6 py-3'>
                          {procedureJourneyLoading === row.id && !procedureJourney ? (
                            <div className='flex items-center gap-2 py-2 text-xs text-muted-foreground'>
                              <Loader2 className='h-3.5 w-3.5 animate-spin' />
                              Loading forms…
                            </div>
                          ) : !procedureJourney ? (
                            <p className='py-2 text-xs text-muted-foreground'>
                              Couldn&apos;t load this {copy.one}&apos;s forms.
                            </p>
                          ) : procedureJourney.steps.length === 0 ? (
                            <p className='py-2 text-xs text-muted-foreground'>
                              This {copy.one} has no forms yet.
                            </p>
                          ) : (
                            <ul className='divide-y'>
                              {procedureJourney.steps.map(step => {
                                const stepMeta = STATUS_META[step.status]
                                return (
                                  <li key={step.stepId} className='flex items-center gap-3 py-1.5'>
                                    <span className='min-w-0 truncate text-sm'>
                                      {step.formName}{' '}
                                      <span className='text-xs text-muted-foreground'>({versionLabel(step.formVersion)})</span>
                                    </span>
                                    {/* How many times this form runs in THIS run,
                                        editable in place. Only on a step that
                                        actually repeats — a "1" beside every
                                        other form would be noise, and the spec
                                        shows the control only where repeat > 1. */}
                                    {isRepeatingStep(step) && (
                                      <StepRepeatCell
                                        procedureId={row.id}
                                        step={step}
                                        onSaved={reloadAll}
                                      />
                                    )}
                                    {/* Per-form menu, mirroring the row's — the
                                        spec is "applied to Form and Transplant",
                                        and a sheet for ONE form is the narrower
                                        pick an operator usually wants.

                                        Gated on the same rule, per FORM: leaving
                                        these visible under a hidden row-level
                                        menu would contradict it one click later,
                                        since a procedure has a process step only
                                        if one of these forms does. */}
                                    <span className='flex-1'>
                                      {step.hasProcessStep !== false && (
                                        <PickListActionsMenu
                                          scope={{
                                            scope: 'FORM',
                                            procedureId: row.id,
                                            formDefinitionId: step.formDefinitionId,
                                            ...(step.submissionId
                                              ? { submissionId: step.submissionId }
                                              : {}),
                                          }}
                                          open={openMenuRowId === `${row.id}:${step.stepId}`}
                                          onOpenChange={next =>
                                            setOpenMenuRowId(next ? `${row.id}:${step.stepId}` : null)
                                          }
                                        />
                                      )}
                                    </span>
                                    <span className={cn('w-24 shrink-0 text-[11px]', stepMeta.text)}>
                                      {stepMeta.label}
                                    </span>
                                    {canAssign ? (
                                      <span className='w-48 shrink-0'>
                                        <AssigneeSelect
                                          users={assignableUsers}
                                          value={step.assignee ?? null}
                                          disabled={assignSavingKey === `${row.id}:${step.stepId}`}
                                          onChange={assigneeId => saveStepAssign(row, step, assigneeId)}
                                          buttonClassName='h-7 px-2 text-xs'
                                        />
                                      </span>
                                    ) : (
                                      <span
                                        className={cn(
                                          'w-40 shrink-0 truncate text-xs',
                                          step.assignee ? 'font-medium' : 'text-muted-foreground',
                                        )}
                                        title={step.assignee?.name}
                                      >
                                        {step.assignee?.name ?? '—'}
                                      </span>
                                    )}
                                  </li>
                                )
                              })}
                            </ul>
                          )}
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
            </div>
          </TooltipProvider>
        )}

        {!pageLoading && pagedTotal > 0 && (
          <div className='mt-3 flex items-center justify-between rounded-lg border bg-card px-4 py-2 text-xs text-muted-foreground'>
            <span>
              {/* Counts PROCEDURES on every page, the CAPA one included: paging
                  is server-side and by procedure, so a run holding two CAPAs is
                  two rows against one count. Saying "procedures" keeps the
                  number honest about what the pager moves through. */}
              {skip + 1}–{Math.min(skip + take, pagedTotal)} of {pagedTotal} procedures
            </span>
            <div className='flex items-center gap-2'>
              <select
                value={take}
                onChange={e => { setTake(Number(e.target.value)); setSkip(0) }}
                className='rounded border bg-background px-2 py-0.5 text-xs'
              >
                {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n} / page</option>)}
              </select>
              <div className='flex items-center gap-1'>
                <button
                  className='inline-flex items-center gap-1 rounded-md px-2 py-1 hover:bg-accent disabled:opacity-40'
                  disabled={skip === 0}
                  onClick={() => setSkip(s => Math.max(0, s - take))}
                >
                  <ChevronLeft className='h-3.5 w-3.5' />
                  Previous
                </button>
                <button
                  className='inline-flex items-center gap-1 rounded-md px-2 py-1 hover:bg-accent disabled:opacity-40'
                  disabled={skip + take >= pagedTotal}
                  onClick={() => setSkip(s => s + take)}
                >
                  Next
                  <ChevronRight className='h-3.5 w-3.5' />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <Dialog open={!!deleteTarget} onOpenChange={open => !open && setDeleteTarget(null)}>
        <DialogContent className='sm:max-w-md'>
          <DialogHeader>
            <DialogTitle>Cancel Procedure</DialogTitle>
            <DialogDescription>
              Cancel the <strong>{deleteTarget?.procedureTemplateName}</strong> procedure for{' '}
              <strong>{deleteTarget?.patientName ?? deleteTarget?.mrn}</strong>? Its open forms are
              voided. Nothing is deleted: every form and its audit trail is kept, and the
              cancellation is recorded with your name and reason.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-1.5'>
            <label htmlFor='cancel-procedure-reason' className='text-sm font-medium'>
              Reason
            </label>
            <Textarea
              id='cancel-procedure-reason'
              rows={3}
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              placeholder='Why is this procedure being cancelled?'
            />
          </div>
          {deleteError && (
            <div className='flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive'>
              <AlertCircle className='mt-0.5 h-4 w-4 shrink-0' />
              {deleteError}
            </div>
          )}
          <DialogFooter>
            <Button variant='outline' onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Keep procedure
            </Button>
            <Button
              variant='destructive'
              onClick={() => deleteTarget && confirmDelete(deleteTarget)}
              disabled={deleting || cancelReason.trim() === ''}
            >
              {deleting && <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />}
              Cancel procedure
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onOpenChange={open => { setCreateOpen(open); if (!open) clearCreateError() }}>
        {/* Padding lives on the three bands, not the shell, so the body can
            scroll UNDER a header and footer that stay put. The form runs to
            seven fields on a transplant; with one scrolling box the Create
            button was the first thing to leave the screen. */}
        <DialogContent className='gap-0 overflow-hidden p-0 sm:max-w-[560px]'>
          <DialogHeader className='space-y-1 border-b px-6 py-5'>
            {/* The dialog names the same thing its button did. "Create kit
                order" opening "Create KIT procedure" reads as though the click
                went somewhere unintended. */}
            <DialogTitle>
              {copy.create}
            </DialogTitle>
            <DialogDescription>
              {createProcedureType === KIT_TYPE
                ? 'Pick a KIT template. Assign the order afterwards, to execute it.'
                : isMes
                  ? // No patient to search for: an MES order gets one from an Epic
                    // order, or never (z8nrz7ckj9).
                    'Pick a template to start a manufacturing order. Its MFG number is assigned automatically.'
                  : 'Search for an existing patient and select a template to start a new transplant.'}
            </DialogDescription>
          </DialogHeader>

          <div className='max-h-[min(68vh,34rem)] space-y-5 overflow-y-auto px-6 py-5'>
            {/* Patient typeahead — absent for a KIT, which has no patient. */}
            <div
              className={cn(
                'space-y-2',
                // An MES order has no patient to enter either: it acquires one
                // from an Epic order, or never (z8nrz7ckj9).
                (createProcedureType === KIT_TYPE || createProcedureType === MES_TYPE) &&
                  'hidden',
              )}
            >
              {/* The confirmation rides ON the label row rather than as a line
                  of grey text under the field. It repeats what the input
                  already shows, so it has earned a mark, not a sentence. */}
              <div className='flex min-h-[1.25rem] items-center justify-between gap-2'>
                <label className='text-sm font-medium'>Patient</label>
                {selectedPatientMrn && (
                  <span className='flex items-center gap-1 text-[11px] font-medium text-emerald-700'>
                    <Check className='h-3 w-3' />
                    <span className='font-mono'>{selectedPatientMrn}</span>
                  </span>
                )}
              </div>
              <div className='relative' ref={dropdownRef}>
                <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none' />
                <Input
                  ref={patientSearchRef}
                  className='pl-9'
                  placeholder='Search by MRN or name…'
                  value={patientSearch}
                  aria-invalid={shownErrorField === 'patient'}
                  onChange={e => {
                    setPatientSearch(e.target.value)
                    setSelectedPatientMrn('')
                    setPatientDropdownOpen(true)
                    clearCreateError()
                  }}
                  onFocus={() => setPatientDropdownOpen(true)}
                  onBlur={e => {
                    // Delay so clicks inside the dropdown can fire first.
                    if (!dropdownRef.current?.contains(e.relatedTarget as Node)) {
                      setTimeout(() => setPatientDropdownOpen(false), 150)
                    }
                  }}
                  autoComplete='off'
                />
                {patientDropdownOpen && filteredPatientOptions.length > 0 && (
                  <div className='absolute z-50 mt-1 max-h-56 w-full overflow-auto rounded-lg border bg-popover shadow-md'>
                    {filteredPatientOptions.map(opt => (
                      <button
                        key={opt.mrn}
                        type='button'
                        className={cn(
                          'flex w-full items-center px-3 py-2 text-left text-sm hover:bg-muted',
                          selectedPatientMrn === opt.mrn && 'bg-muted font-medium',
                        )}
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => {
                          setSelectedPatientMrn(opt.mrn)
                          setPatientSearch(opt.label)
                          setPatientDropdownOpen(false)
                          clearCreateError()
                        }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
                {patientDropdownOpen && allPatientsLoading && filteredPatientOptions.length === 0 && (
                  <div className='absolute z-50 mt-1 flex w-full items-center gap-2 rounded-lg border bg-popover px-3 py-2 text-sm text-muted-foreground shadow-md'>
                    <Loader2 className='h-3.5 w-3.5 animate-spin' />
                    Loading patients…
                  </div>
                )}
                {patientDropdownOpen && !allPatientsLoading && patientSearch.trim() && filteredPatientOptions.length === 0 && (
                  <div className='absolute z-50 mt-1 w-full rounded-lg border bg-popover px-3 py-2 text-sm text-muted-foreground shadow-md'>
                    No patients match "{patientSearch.trim()}".
                  </div>
                )}
              </div>
              <CreateFieldError show={shownErrorField === 'patient'} message={createError} />
            </div>

            {/* Procedure template select — scoped to the type chosen above. */}
            <div className='space-y-2'>
              <div className='flex min-h-[1.25rem] items-center justify-between gap-2'>
                <label className='text-sm font-medium'>Template</label>
                {selectedProcedureTemplateId && (
                  <span className='flex min-w-0 items-center gap-1 text-[11px] font-medium text-emerald-700'>
                    <Check className='h-3 w-3 shrink-0' />
                    <span className='truncate font-mono'>{selectedTemplate?.name}</span>
                  </span>
                )}
              </div>
              <div className='relative' ref={templateDropdownRef}>
                <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none' />
                <Input
                  className='pl-9'
                  placeholder={copy.pickTemplate}
                  value={templateSearch}
                  aria-invalid={shownErrorField === 'template'}
                  onChange={e => {
                    setTemplateSearch(e.target.value)
                    setSelectedProcedureTemplateId('')
                    setTemplateDropdownOpen(true)
                    clearCreateError()
                  }}
                  onFocus={() => setTemplateDropdownOpen(true)}
                  onBlur={e => {
                    if (!templateDropdownRef.current?.contains(e.relatedTarget as Node)) {
                      setTimeout(() => setTemplateDropdownOpen(false), 150)
                    }
                  }}
                  autoComplete='off'
                />
                {templateDropdownOpen && createTypeTemplates.length > 0 && (
                  <div className='absolute z-50 mt-1 max-h-56 w-full overflow-auto rounded-lg border bg-popover shadow-md'>
                    {createTypeTemplates.map(p => (
                      <button
                        key={p.id}
                        type='button'
                        className={cn(
                          'flex w-full items-center px-3 py-2 text-left text-sm hover:bg-muted',
                          selectedProcedureTemplateId === p.id && 'bg-muted font-medium',
                        )}
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => {
                          setSelectedProcedureTemplateId(p.id)
                          setTemplateSearch(`${p.name} (${versionLabel(p.version)})`)
                          setTemplateDropdownOpen(false)
                          clearCreateError()
                        }}
                      >
                        {p.name} ({versionLabel(p.version)})
                      </button>
                    ))}
                  </div>
                )}
                {templateDropdownOpen && !templateSearchLoading && createTypeTemplates.length === 0 && debouncedTemplateSearch.trim() && (
                  <div className='absolute z-50 mt-1 w-full rounded-lg border bg-popover px-3 py-2 text-sm text-muted-foreground shadow-md'>
                    No {PROCEDURE_TYPE_LABELS[createProcedureType]} templates match &ldquo;{debouncedTemplateSearch.trim()}&rdquo;.
                  </div>
                )}
              </div>
              <CreateFieldError show={shownErrorField === 'template'} message={createError} />
            </div>

            {/* Planning dates — both optional; the server also enforces the order.
                The rule is the divide between what the run cannot be created
                without (patient, template) and the planning detail that follows;
                it carries the grouping so no field needs a label above its
                label. */}
            <div className='grid grid-cols-2 gap-3 border-t pt-5'>
              <div className='space-y-2'>
                <label className='text-sm font-medium' htmlFor='create-scheduled-start'>
                  Scheduled start{' '}
                  <span className='font-normal text-muted-foreground'>(optional)</span>
                </label>
                <Input
                  id='create-scheduled-start'
                  type='datetime-local'
                  value={createScheduledDate}
                  // Past days greyed out and unselectable, matching the table's
                  // Scheduled cell. Enforced on submit below too — `min` binds
                  // the calendar, not the keyboard.
                  min={todayStart}
                  aria-invalid={shownErrorField === 'scheduledDate'}
                  onChange={e => {
                    setCreateScheduledDate(e.target.value)
                    clearCreateError()
                  }}
                />
                <CreateFieldError
                  show={shownErrorField === 'scheduledDate'}
                  message={createError}
                />
              </div>
              <div className='space-y-2'>
                <label className='text-sm font-medium' htmlFor='create-due-date'>
                  Due <span className='font-normal text-muted-foreground'>(optional)</span>
                </label>
                <Input
                  id='create-due-date'
                  type='datetime-local'
                  value={createDueDate}
                  // Earlier than the start is unselectable rather than rejected
                  // on submit. The server still refuses it — this only saves the
                  // operator the round trip.
                  min={createScheduledDate || todayStart}
                  aria-invalid={shownErrorField === 'dueDate'}
                  onChange={e => {
                    setCreateDueDate(e.target.value)
                    clearCreateError()
                  }}
                />
                <CreateFieldError show={shownErrorField === 'dueDate'} message={createError} />
                {/* Left blank, the offset below fills this in on save — say so,
                    or an empty field reads as "no due date". */}
                {!createDueDate && (
                  <p className='text-[11px] text-muted-foreground'>
                    Left blank, the due offset sets this.
                  </p>
                )}
              </div>
            </div>

            {/* DUE OFFSET — per procedure, replacing what used to be one
                tenant-wide number in Configure → General. An offset belongs to
                the work being done: a collection and a full transplant are not
                due in the same number of days.

                Hidden once a due date is typed, because then it decides
                nothing: an explicit date is an override the server keeps
                through re-scheduling, so showing a live offset field beside it
                would imply the two are still negotiating. */}
            {!createDueDate && (
              <div className='space-y-2'>
                <label className='text-sm font-medium' htmlFor='create-due-offset'>
                  Due in <span className='font-normal text-muted-foreground'>(optional)</span>
                </label>
                <div className='flex items-center gap-2'>
                  <Input
                    id='create-due-offset'
                    type='number'
                    min={0}
                    max={MAX_DUE_OFFSET_DAYS}
                    step={1}
                    value={createDueOffset}
                    aria-invalid={shownErrorField === 'dueOffset'}
                    onChange={e => {
                      setCreateDueOffset(e.target.value)
                      clearCreateError()
                    }}
                    placeholder={
                      templateDueOffset == null ? '—' : String(templateDueOffset)
                    }
                    className='w-24'
                  />
                  <span className='text-sm text-muted-foreground'>
                    days after the scheduled start
                  </span>
                </div>
                <CreateFieldError
                  show={shownErrorField === 'dueOffset'}
                  message={createError}
                />
                {/* Zero is a real value, so the empty state has to be named — a
                    blank number field cannot say "use the template's" alone. */}
                <p className='text-[11px] text-muted-foreground'>
                  {templateDueOffset == null
                    ? 'This template sets no default, so leaving this blank derives no due date.'
                    : `Leave blank to use this template's default of ${templateDueOffset} ${
                        templateDueOffset === 1 ? 'day' : 'days'
                      }. Enter 0 for due the same day.`}
                </p>
              </div>
            )}

            {/* How many times each repeating form runs in THIS procedure
                (z8nrz7dkkc). Shown only when the chosen template actually has
                one — asking "how many repeats?" of a template with none is a
                question with no answer.

                Distinct from Qty below: that is how many units the run
                produces, this is how many times one form is performed. */}
            {repeatSteps.length > 0 && (
              <div className='border-t pt-5'>
                {/* The one boxed thing in the dialog, and it earns the box: it
                    is a list of forms inside a form, so without an edge its
                    rows read as more top-level fields. Everything else here is
                    separated by rules alone. */}
                <div className='overflow-hidden rounded-lg border'>
                  <div className='flex gap-2.5 bg-muted/40 px-3.5 py-3'>
                    <Repeat2 className='mt-0.5 h-4 w-4 shrink-0 text-muted-foreground' />
                    <div className='space-y-1'>
                      <p className='text-sm font-semibold leading-none'>Repeats</p>
                      <p className='text-[11px] leading-relaxed text-muted-foreground'>
                        These forms are performed more than once. Each repeat is
                        filled and signed separately, and the next form opens only
                        once every repeat is complete.
                      </p>
                    </div>
                  </div>
                  <div className='divide-y border-t'>
                    {repeatSteps.map(step => (
                      <div key={step.stepId} className='flex items-center gap-3 px-3.5 py-2'>
                        <span className='min-w-0 flex-1 truncate text-sm' title={step.formName}>
                          {step.formName}
                        </span>
                        <Input
                          type='number'
                          min={1}
                          step={1}
                          className='h-8 w-16 text-center tabular-nums'
                          // Falls back to the template's number, so leaving the row
                          // alone means "as the template says" rather than silently
                          // pinning the run to whatever was on screen.
                          value={createRepeats[step.stepId] ?? String(step.repeatCount ?? 1)}
                          onChange={e =>
                            setCreateRepeats(prev => ({ ...prev, [step.stepId]: e.target.value }))
                          }
                        />
                        <span className='w-10 shrink-0 text-xs text-muted-foreground'>times</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Quantity — every type, defaulting to 1 ("For ALL to add Qty = 1
                as a default including Transplant"). Above 1 it multiplies every
                step's inventory requirement, which is why a KIT run says so. */}
            <div
              className={cn(
                // The rule opens the "what this run produces" zone. The repeats
                // card carries its own when it is present, so two rules never
                // stack.
                repeatSteps.length === 0 && 'border-t pt-5',
                // Kit and MES: the customer sits beside the quantity rather than
                // taking a row of its own (z8nrz7e714).
                (canPickCustomer || canPickProduct) && 'grid grid-cols-2 items-start gap-3',
              )}
            >
              <div className='space-y-2'>
                <label className='text-sm font-medium' htmlFor='create-quantity'>
                  Quantity
                </label>
                {/* Sized to its content. A number that is almost always 1 in a
                    full-width box was the widest, loudest control in the dialog
                    and the least often changed. */}
                <div className='flex items-center gap-2'>
                  <Input
                    id='create-quantity'
                    type='number'
                    min={1}
                    max={MAX_PROCEDURE_QUANTITY}
                    step={1}
                    className='h-9 w-24 tabular-nums'
                    value={createQuantity}
                    aria-invalid={shownErrorField === 'quantity'}
                    onChange={e => {
                      setCreateQuantity(e.target.value)
                      clearCreateError()
                    }}
                  />
                  <span className='text-sm text-muted-foreground'>
                    {createProcedureType === KIT_TYPE ? 'kits' : 'units'}
                  </span>
                </div>
                <CreateFieldError show={shownErrorField === 'quantity'} message={createError} />
                <p className='text-[11px] leading-relaxed text-muted-foreground'>
                  {/* The unit is on the field now, so the sentence no longer has
                      to name it — it says what the number DOES instead. */}
                  {createProcedureType === KIT_TYPE
                    ? 'The bill of materials is multiplied by this, and each kit is tracked individually.'
                    : 'Every step’s inventory requirement is multiplied by this.'}
                </p>
              </div>

              <div className='space-y-4'>
              {/* Who the run is made for (z8nrz7e714). Optional, so it never
                  gates Create, and absent on patient care, which has a patient. */}
              {canPickCustomer && (
                <div className='space-y-2'>
                  <div className='flex items-center justify-between gap-2'>
                    <span className='text-sm font-medium'>
                      {copy.customer}{' '}
                      <span className='font-normal text-muted-foreground'>(optional)</span>
                    </span>
                    {createCustomerId && (
                      <button
                        type='button'
                        className='text-xs text-muted-foreground hover:text-foreground hover:underline'
                        onClick={() => setCreateCustomerId('')}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <SearchableSelect
                    value={createCustomerId}
                    onChange={setCreateCustomerId}
                    options={customerOptions}
                    placeholder={customersQuery.isLoading ? 'Loading customers…' : 'No customer'}
                    disabled={customersQuery.isLoading}
                    emptyMessage={
                      customersQuery.isError
                        ? 'Could not load customers.'
                        : 'No customers yet — add a partner in the Customer category under Configure → Partners.'
                    }
                    triggerTitle={copy.customer}
                  />
                </div>
              )}

              {/* The Product Code the order makes (z8nrz7fvhx), under Customer as
                  in the spec's mock. Optional; MES only. */}
              {canPickProduct && (
                <div className='space-y-2'>
                  <div className='flex items-center justify-between gap-2'>
                    <span className='text-sm font-medium'>
                      Product <span className='font-normal text-muted-foreground'>(optional)</span>
                    </span>
                    {createMesProductId && (
                      <button
                        type='button'
                        className='text-xs text-muted-foreground hover:text-foreground hover:underline'
                        onClick={() => setCreateMesProductId('')}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <Combobox
                    id='create-product'
                    value={createMesProductId}
                    onChange={setCreateMesProductId}
                    options={productOptions}
                    placeholder={
                      productsQuery.isLoading
                        ? 'Loading products…'
                        : productsQuery.isError
                          ? 'Could not load products.'
                          : productOptions.length === 0
                            ? 'No products yet — add one under Configure → Product Code.'
                            : 'Product'
                    }
                    title={productOptions.find(o => o.value === createMesProductId)?.hint ?? 'Product'}
                  />
                </div>
              )}
              </div>
            </div>

            {/* Work order — offered on BOTH branches, kit and transplant, which
                is the client's rule: "it's during the procedure creation,
                whether for kit or for transplant". The two branches differ only
                in what a BLANK means, since only a Transplant mints one. Same
                number is editable afterwards in the WO# column. */}
            <div className='space-y-2 border-t pt-5'>
              <label className='text-sm font-medium' htmlFor='create-work-order'>
                Work Order #{' '}
                <span className='font-normal text-muted-foreground'>(optional)</span>
              </label>
              <Input
                id='create-work-order'
                value={createWorkOrder}
                maxLength={MAX_WORK_ORDER_LENGTH}
                placeholder='e.g. WO-26-0043'
                aria-invalid={shownErrorField === 'workOrder'}
                onChange={e => {
                  setCreateWorkOrder(e.target.value)
                  clearCreateError()
                }}
              />
              <CreateFieldError show={shownErrorField === 'workOrder'} message={createError} />
              <p className='text-[11px] leading-relaxed text-muted-foreground'>
                {createProcedureType === KIT_TYPE
                  ? 'Enter the work order this run is raised against. Leave blank if it has none — a kit run is identified by its LOT number, so nothing is generated here.'
                  : createProcedureType === MES_TYPE
                    ? 'Enter the work order this order is raised against. Leave blank if it has none — an MES order is identified by its MFG number, which is assigned for you.'
                    : 'Leave blank to generate one automatically. Enter a number to match a work order issued outside this system.'}
              </p>
            </div>

            {/* Given a tint and an edge so a refusal is not one more grey line
                in a form made of grey lines.

                Only for a refusal that could NOT be pinned to an input — one
                that could is rendered against that input instead, and showing
                it in both places would read as two separate problems
                (z8nrz7dyc2, AC3.1). */}
            {createError && shownErrorField === null && (
              <p
                role='alert'
                className='flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive'
              >
                <AlertCircle className='mt-px h-3.5 w-3.5 shrink-0' />
                {createError}
              </p>
            )}
          </div>

          {/* Outside the scrolling body: on a transplant this form is long
              enough that Create used to sit below the fold. */}
          <DialogFooter className='border-t bg-muted/20 px-6 py-4'>
            <Button variant='ghost' onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={submitCreateProcedure}
              disabled={!createIsSubmittable || creating}
            >
              {creating && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
              {copy.create}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Asked BEFORE the schedule is written — see `confirmMove`. Renders
          ahead of the reserve dialog because that is the order they appear in:
          agree to the move, then decide what to book for it. */}
      <MoveRunConfirmDialog
        open={!!moveConfirm}
        procedureName={moveConfirm?.row.procedureTemplateName ?? 'This run'}
        from={moveConfirm?.row.scheduledDate ?? null}
        to={moveConfirm?.to ?? null}
        holds={moveHolds}
        loading={moveHoldsLoading}
        error={null}
        onConfirm={() => answerMove(true)}
        onCancel={() => answerMove(false)}
      />

      {/* A run's Product Code (z8nrz7fvhx). Save edits the shared record, so
          the list is refetched to show the new code and description. */}
      <MesProductDialog
        open={!!productInfo}
        onOpenChange={o => !o && setProductInfo(null)}
        productId={productInfo?.id ?? null}
        mfgNumber={productInfo?.mfgNumber}
        canEdit={can(PERMISSIONS.productCodesManage)}
        onSaved={() => load()}
      />

      {/* The CAPA page's own create: raise a capa form on a run in progress,
          which is what the "+" menu on a procedure page does. */}
      <CreateCapaDialog
        open={createCapaOpen}
        onClose={() => setCreateCapaOpen(false)}
        targets={capaTargets}
        onCreated={({ target, submissionId }) => {
          setCreateCapaOpen(false)
          const path = procedureRunPath(
            target.procedureTemplate.type,
            target.id,
            target.patientMrn,
          )
          // No page to open (patient care with no patient): stay on the list and
          // show the new CAPA there rather than navigating nowhere.
          if (!path) {
            reloadAll()
            return
          }
          navigate(withOrigin(withQuery(path, adHocStepQuery(submissionId))))
        }}
      />

      {/* Offered after a schedule is saved — see `openSchedulePlan`. */}
      <ScheduleReserveDialog
        open={!!planFor}
        procedureName={planFor?.procedureTemplateName ?? 'This run'}
        plan={plan}
        loading={planLoading}
        busy={planBusy}
        error={planError}
        onConfirm={confirmSchedulePlan}
        onSkip={() => {
          setPlanFor(null)
          setPlanError(null)
        }}
      />
    </div>
  )
}
